import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/EditOutlined";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import RefreshIcon from "@material-ui/icons/Refresh";
import ReplayIcon from "@material-ui/icons/ReplayOutlined"; // "Executar novamente"
import SwapIcon from "@material-ui/icons/CachedOutlined"; // "trocar gatilho" (igual ao Builder)
import SendIcon from "@material-ui/icons/SendOutlined"; // "enviar teste"
import CodeIcon from "@material-ui/icons/Code";
import InfoIcon from "@material-ui/icons/InfoOutlined"; // descrição do campo (hover)
import Tooltip from "@material-ui/core/Tooltip";
import CopyIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import VarIcon from "./PickVarIcon"; // "escolher um dado do fluxo" (quadrado tracejado + cursor)
import TuneIcon from "@material-ui/icons/TuneOutlined"; // seção "Avançado"
import MusicIcon from "@material-ui/icons/MusicNoteOutlined";
import PlayArrowIcon from "@material-ui/icons/PlayArrowOutlined";
import StopIcon from "@material-ui/icons/StopOutlined";
import ClearIcon from "@material-ui/icons/ClearOutlined";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { toast } from "react-toastify";
import { format } from "date-fns";

import api from "../../services/api";
import AttioValues from "./AttioValues";
import { connectToSocket } from "../../services/socket";
import toastError from "../../errors/toastError";
import { VERBS, podeReexecutar } from "./verbs";
import { TRIGGERS, isWebhookBased } from "./triggers";
import { i18n } from "../../translate/i18n";
import JsonTree from "../../components/JsonTree";
import RecordingPlayer from "../../components/RecordingPlayer";
import CollectorSample from "./CollectorSample";
import CodeEditor from "../../components/CodeEditor";
import NumberPickerModal from "./NumberPickerModal";
import { useConfirm } from "../../components/ConfirmDialog";
import { useProject } from "../../context/Project/ProjectContext";
import { useMenuStyles } from "../../components/menuStyles";
import VoicePicker from "./VoicePicker";
import AudioPicker from "./AudioPicker";
import { useAudioPreview } from "../../utils/audioPreview";
import { formatPhone } from "../../utils/formatPhone";
import TriggerTestModal from "./TriggerTestModal";
import HttpTestModal from "./HttpTestModal";
import ToolInputEditor from "./ToolInputEditor";
import AgentSelect from "./AgentSelect";
import RouteAgentEditor from "./RouteAgentEditor";
import NumberSelect from "../../components/NumberSelect";
import VariablePicker from "./VariablePicker";
import ConnectorModal from "../Connectors/ConnectorModal";
import { byType as connByType } from "../Connectors/catalog";
import { connectorHealth } from "../Connectors/connectorHealth";
import NodeConfigShell from "./NodeConfigShell";
import NodeIcon from "./NodeIcon";
import VarChip from "./VarChip";
import TokenTextArea from "./TokenTextArea";
import { nodeAccent } from "./CustomNodes";

const useStyles = makeStyles(theme => ({
  root: {
    width: 360,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.app.border}`,
    background: "#fff",
    padding: theme.spacing(2),
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      borderLeft: "none",
      borderTop: `1px solid ${theme.app.border}`,
    },
  },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(1) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  editBtn: { color: theme.app.dark, fontWeight: 700, textTransform: "none", padding: "2px 8px", minWidth: 0 },
  field: {
    marginBottom: theme.spacing(2),
    // evita o label longo estourar/quebrar linha sobre o input (ex.: "Caminho…")
    "& .MuiInputLabel-root": { maxWidth: "calc(100% - 32px)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    "& .MuiInputLabel-shrink": { maxWidth: "calc(133% - 32px)" },
  },
  advToggle: {
    display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none",
    color: theme.app.textSecondary, fontSize: 12.5, fontWeight: 600, margin: theme.spacing(0.5, 0, 1.5),
    "& svg": { fontSize: 16 },
  },
  hint: { color: theme.app.textTertiary, fontSize: 12, marginBottom: theme.spacing(1) },
  infoIcon: {
    fontSize: 15,
    marginLeft: 4,
    verticalAlign: "-2px",
    color: theme.app.textTertiary,
    cursor: "help",
    "&:hover": { color: theme.app.textSecondary },
  },
  infoTip: { fontSize: 12, lineHeight: 1.5, padding: theme.spacing(0.5, 0.25) },
  leadText: { color: theme.app.textSecondary, fontSize: 12.5, lineHeight: 1.5, marginBottom: theme.spacing(2) },
  codeToggle: { color: theme.app.textTertiary, padding: 4, "&:hover": { color: theme.app.ink } },
  // caixa do input quando exibe um chip: o input real tem largura 0; chip à esquerda
  chipInput: { paddingRight: 4, "& .MuiInputAdornment-positionStart": { marginRight: 6, maxWidth: "calc(100% - 40px)" } },
  caseRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" },
  // prévia dos balões da "Mensagem de abertura" (nó Iniciar conversa): cada bloco
  // separado por linha em branco vira um balão — deixa claro que é outra mensagem.
  bubblePrev: { marginTop: 10, display: "flex", flexDirection: "column", gap: 7, alignItems: "flex-end" },
  bubblePrevHead: { alignSelf: "flex-start", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: theme.app.textTertiary },
  bubble: {
    maxWidth: "88%", background: "rgba(37,211,102,0.14)", color: theme.app.ink, fontSize: 13, lineHeight: 1.4,
    padding: "7px 11px", borderRadius: 12, borderTopRightRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-word",
    boxShadow: "0 1px 1px rgba(0,0,0,0.05)", animation: "$bubbleIn .18s ease",
  },
  bubbleTip: { alignSelf: "flex-start", fontSize: 11.5, color: theme.app.textTertiary, marginTop: 1 },
  "@keyframes bubbleIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
  urlBox: {
    background: theme.app.chatBg,
    border: `1px solid ${theme.app.border}`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
    marginBottom: theme.spacing(1),
  },
  // URL com botão de copiar embutido à direita, dentro da própria caixa
  copyField: {
    display: "flex",
    alignItems: "center",
    background: theme.app.chatBg,
    border: `1px solid ${theme.app.border}`,
    borderRadius: 8,
    padding: theme.spacing(0.5, 0.5, 0.5, 1.5),
    marginBottom: theme.spacing(1.5),
  },
  copyFieldText: {
    flex: 1,
    minWidth: 0, // permite o ellipsis funcionar dentro do flex
    fontFamily: "monospace",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyFieldBtn: {
    flexShrink: 0,
    marginLeft: theme.spacing(1),
    padding: 6,
    borderRadius: 6,
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.main,
    "&:hover": { background: theme.palette.primary.dark },
  },
  section: { borderTop: `1px solid ${theme.app.border}` },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: theme.spacing(1.25, 0),
    userSelect: "none",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: theme.app.textTertiary,
  },
  grow: { flex: 1, minWidth: 0 },
  // linha das abas Entrada/Saída com o "Executar novamente" à direita
  ioHead: { display: "flex", alignItems: "center", gap: 4 },
  sectionBody: { paddingBottom: theme.spacing(2) },
  evItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1, 1),
    borderRadius: 8,
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    "&:hover": { background: theme.app.selectedRow },
  },
  evActive: { background: theme.app.selectedRow, borderLeft: `3px solid ${theme.app.lime}` },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  dotOk: { background: "#1E7E34" },
  dotErr: { background: "#B12A2A" },
  dotInfo: { background: theme.app.textTertiary },
  evMsg: { flex: 1, fontSize: 12.5, color: theme.app.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  evTime: { fontSize: 11, color: theme.app.textTertiary, fontVariantNumeric: "tabular-nums" },
}));

const TRIGGER_EVENT_TYPES = ["webhook.received", "trigger.tested"];
const isTriggerEvent = ev => TRIGGER_EVENT_TYPES.indexOf(ev.type) >= 0;

// amostra de variáveis ({{steps.trigger.input.*}}) a partir dos parâmetros de
// uma Ferramenta, pra alimentar o VariablePicker / aba Resultado.
const toolSampleOf = params => {
  const input = {};
  (params || []).forEach(p => {
    if (!p.name) return;
    input[p.name] = p.type === "number" ? 0 : p.type === "boolean" ? true : "exemplo";
  });
  return { input };
};

// Campo somente-leitura de URL com o "copiar" dentro do input (ícone à direita).
// Ao copiar, o ícone vira um check e um tooltip "Copiado" aparece por ~1,8s.
const CopyUrlField = ({ value, classes }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
  };

  return (
    <div className={classes.copyField}>
      <span className={classes.copyFieldText} title={value}>{value}</span>
      <Tooltip title={i18n.t("apps.copiedShort")} placement="top" open={copied} arrow>
        <IconButton
          size="small"
          className={classes.copyFieldBtn}
          onClick={handleCopy}
          aria-label={i18n.t("apps.copy")}
        >
          {copied ? <CheckIcon style={{ fontSize: 16 }} /> : <CopyIcon style={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </div>
  );
};

// Descrição longa do campo num ÍCONE + hover, em vez de um parágrafo debaixo do
// input: o painel tem 360px e dois ou três helperTexts já viram uma parede de
// texto. Fica como `info` no def do verbo; `helperText` continua para o aviso
// curto que precisa estar sempre visível (ex.: um mínimo que derruba a chamada).
const FieldInfo = ({ text, classes }) =>
  text ? (
    <Tooltip
      arrow
      interactive
      placement="top"
      enterTouchDelay={0}
      title={<div className={classes.infoTip}>{text}</div>}
    >
      <InfoIcon className={classes.infoIcon} />
    </Tooltip>
  ) : null;

const ConfigDrawer = ({
  node,
  onChange,
  onDelete,
  onSwapNode,
  onPickTrigger,
  webhookUrl,
  published,
  dirty,
  onPublish,
  appId,
  onSetSample,
  onNodeTested,
  scope,
  varGroups,
  varRunAt,
  readOnly,
  onEdit,
  runStepData,
  runContext,
  inspecting,
  selectedRunId,
  selectedStep,
  projectId,
  onTestTool,
  onClose,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { confirm } = useConfirm();
  const { projects, activeProjectId } = useProject();
  const activeProject = (projects || []).find(p => p.id === activeProjectId) || null;
  const menuCls = useMenuStyles();
  // dropdown dos selects deste painel com o MESMO visual dos menus de contexto
  // do app (ver memória "menu-modal-design-pattern"). Passar em SelectProps.
  const selectProps = {
    MenuProps: {
      classes: { paper: menuCls.selectPaper },
      getContentAnchorEl: null, // MUI v4: sem isso o anchorOrigin é ignorado
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
    },
  };
  const [ioTab, setIoTab] = useState(0);
  const [rerunning, setRerunning] = useState(false); // "Executar novamente" em curso
  // aba da casca (Configuração | Teste & resultados). Mora AQUI, e não dentro do
  // NodeConfigShell, para a seleção PERSISTIR ao navegar entre nós: cada tipo de
  // nó renderiza uma casca diferente, então um estado interno morreria no remount.
  const [shellTab, setShellTab] = useState("config");

  // "Editar" button shown beside the component title while in view mode:
  // leaves view mode keeping focus on this node (Builder handles the rest).
  const editBtn = readOnly && onEdit ? (
    <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={onEdit} className={classes.editBtn}>
      Editar
    </Button>
  ) : null;

  // Reexecuta a partir do nó selecionado, com os dados da execução aberta. O
  // backend monta o scope pelos eventos daquela execução, roda dali até o fim da
  // sequência e PARA no primeiro nó que exigir a chamada — voz não roda fora dela.
  const rerunStep = async () => {
    if (rerunning) return;
    setRerunning(true);
    try {
      const { data: r } = await api.post(`/applications/${appId}/rerun-step`, {
        runId: selectedRunId,
        step: selectedStep,
      });
      const n = ((r && r.executados) || []).length;
      const parou = r && r.parouEm;
      const quantos = n === 1 ? "1 nó executado" : `${n} nós executados`;
      if (parou) {
        toast.info(`${quantos}. Parou em "${parou.step || parou.verb}": ${parou.motivo}.`);
      } else {
        toast.success(`${quantos}. Veja a execução nova na lista.`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setRerunning(false);
    }
  };

  // Pipedream-style in/out tabs for the selected node in the inspected execution
  const renderRunIO = () => {
    if (!inspecting) return null;
    if (!runStepData) {
      return (
        <div className={classes.section}>
          <Typography className={classes.hint} style={{ padding: "12px 0" }}>
            Este nó não foi executado nesta execução.
          </Typography>
        </div>
      );
    }
    const empty = <Typography className={classes.hint}>—</Typography>;
    // "Executar novamente": roda este nó (e o que vem depois) com os dados DESTA
    // execução. Só aparece quando há o que reexecutar e o nó não depende de uma
    // chamada viva. A execução nova entra na lista sozinha (o painel de execuções
    // recarrega pelo socket e seleciona a mais recente), então não há o que
    // atualizar aqui.
    const temDados = runStepData.input !== undefined || runStepData.output !== undefined;
    const podeRerun =
      temDados && !isTrigger && podeReexecutar(data.verbKey) && !!appId && !!selectedRunId && !!selectedStep;
    return (
      <div className={classes.section}>
        <div className={classes.ioHead}>
          <Tabs
            className={classes.grow}
            value={ioTab}
            onChange={(e, v) => setIoTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="standard"
          >
            <Tab label="Entrada" />
            <Tab label="Saída" />
            <Tab label="Contexto" />
          </Tabs>
          {podeRerun && (
            <Tooltip title="Executar novamente" arrow placement="top">
              {/* span: o Tooltip precisa de um filho que receba eventos, e o
                  IconButton desabilitado (durante a execução) não recebe. */}
              <span>
                <IconButton size="small" disabled={rerunning} onClick={rerunStep} aria-label="Executar novamente">
                  {rerunning ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
        <div className={classes.sectionBody} style={{ paddingTop: 8 }}>
          {ioTab === 0 && (
            runStepData.input !== undefined
              ? <JsonTree data={runStepData.input} rootKey="in" />
              : empty
          )}
          {ioTab === 1 && (
            runStepData.output !== undefined
              ? (
                <>
                  {data.verbKey === "collector_return" && runStepData.output && Array.isArray(runStepData.output.rows) ? (
                    <CollectorSample
                      rows={runStepData.output.rows}
                      count={runStepData.output.count}
                      truncated={runStepData.output.truncated}
                      cursorField={runStepData.output.cursorField}
                    />
                  ) : (
                    <JsonTree data={runStepData.output} rootKey="out" />
                  )}
                  {/* "Ver conversa": nós de IA de WhatsApp (Iniciar conversa /
                      Transferir para a IA) trazem conversationKey+agentId na saída.
                      Abre a aba Conversas do agente já naquela conversa. */}
                  {runStepData.output && runStepData.output.conversationKey && runStepData.output.agentId ? (
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginTop: 10, textTransform: "none" }}
                      onClick={() =>
                        window.open(
                          `/agents/${runStepData.output.agentId}?tab=conversas&conv=${encodeURIComponent(runStepData.output.conversationKey)}`,
                          "_blank",
                          "noopener"
                        )
                      }
                    >
                      Ver conversa →
                    </Button>
                  ) : null}
                  {/* player da gravação: só aparece se a saída trouxe um call_sid
                      (nós de chamada). Carrega o áudio só no clique. */}
                  <RecordingPlayer
                    callSid={
                      (runStepData.output && runStepData.output.callSid) ||
                      (runStepData.output && runStepData.output.call && runStepData.output.call.sid) ||
                      null
                    }
                    applicationId={appId}
                  />
                </>
              )
              : empty
          )}
          {ioTab === 2 && (
            <>
              <Typography className={classes.hint} style={{ marginBottom: 8 }}>
                Tudo que estava disponível para este nó nesta execução (o gatilho e a
                saída dos nós anteriores). É daqui que os campos puxam com{" "}
                <code>{"{{steps.<nó>.<campo>}}"}</code> — veja se o nome do nó bate com o
                que você mapeou.
              </Typography>
              {runContext && runContext.steps ? <JsonTree data={runContext.steps} rootKey="steps" /> : empty}
            </>
          )}
        </div>
      </div>
    );
  };
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [httpTestOpen, setHttpTestOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false); // seção "Avançado" dos campos do nó
  const [newCase, setNewCase] = useState(""); // texto do novo caso do branch (texto ou número)
  const [varField, setVarField] = useState(null);
  const areaInsert = useRef(null); // fn de inserção do TokenTextArea ativo (pro picker inserir o chip no cursor)
  // posição do cursor por campo de texto — pra inserir a variável onde o usuário
  // está, não no fim. Atualizado a cada seleção/clique/tecla no input.
  const caretRef = useRef({});
  const trackCaret = name => {
    const rec = e => { caretRef.current = { field: name, start: e.target.selectionStart, end: e.target.selectionEnd }; };
    return { onSelect: rec, onKeyUp: rec, onClick: rec };
  };
  const [connectors, setConnectors] = useState([]);
  const [collectors, setCollectors] = useState([]); // p/ o campo "collector" (gatilho Coletor de dados)
  const [apps, setApps] = useState([]); // p/ o campo "application" (gatilho por evento de chamada: fluxo de origem)
  const [gcalCalendars, setGcalCalendars] = useState([]); // p/ o campo "gcalCalendar" (nós do Google Agenda)
  const [datasets, setDatasets] = useState([]); // p/ o campo "dataset" (nó Salvar registro)
  const [datasetSchema, setDatasetSchema] = useState(null); // { id, fields } da tabela selecionada
  const [agentDeclaredInputs, setAgentDeclaredInputs] = useState([]); // entradas declaradas do agente do transfer_ai
  const [newConn, setNewConn] = useState(null); // { type, field }
  const [numbers, setNumbers] = useState([]);
  const [numberPicker, setNumberPicker] = useState(null); // field name being picked
  const [audioField, setAudioField] = useState(null); // field name choosing audio
  const [menuModal, setMenuModal] = useState(false); // IVR "message & voice" modal
  const { playingUrl: audioPlaying, toggle: audioToggle } = useAudioPreview();
  const [vendors, setVendors] = useState([]); // available TTS providers
  const [voices, setVoices] = useState(null); // voices of the current provider [{id,name}]
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [extList, setExtList] = useState([]); // extensions (for ring group)

  // load extensions when a node has an "extensions" multi-select field
  useEffect(() => {
    const hasExt = node && VERBS[node.data.verbKey] && (VERBS[node.data.verbKey].fields || []).some(f => f.type === "extensions");
    if (!hasExt) return;
    api.get("/extensions").then(({ data }) => setExtList(data.extensions || [])).catch(() => setExtList([]));
  }, [node]);

  // WhatsApp templates: load APPROVED templates for a node's "whatsappTemplate" field
  const [waTpls, setWaTpls] = useState([]);
  const [waTplLoading, setWaTplLoading] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waTestMsg, setWaTestMsg] = useState(null);
  // número usado SÓ no "Testar envio". Separado de data.destination porque aquele
  // campo normalmente guarda uma variável do fluxo ({{steps.trigger...}}), que não
  // tem dígitos e a Gupshup rejeitava com "Invalid Destination".
  const [waTestTo, setWaTestTo] = useState("");
  useEffect(() => {
    const d = node && node.data;
    const vdef = d && VERBS[d.verbKey];
    const hasTpl = vdef && (vdef.fields || []).some(f => f.type === "whatsappTemplate");
    setWaTestMsg(null);
    if (!hasTpl || !d.connectorId) { setWaTpls([]); return; }
    setWaTplLoading(true);
    api
      .get("/connectors/gupshup/templates", { params: { connectorId: d.connectorId } })
      .then(({ data }) => setWaTpls((data.templates || []).filter(t => t.status === "APPROVED")))
      .catch(() => setWaTpls([]))
      .finally(() => setWaTplLoading(false));
    // depend only on stable keys (node id + connector) — NOT the whole node,
    // which changes on every keystroke and would refetch the list each char.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node && node.id, node && node.data && node.data.connectorId, node && node.data && node.data.verbKey]);

  // a Scheduled trigger starts with a sane default (daily 09:00, Brasília) so it
  // is valid even if the user accepts the defaults without touching anything.
  useEffect(() => {
    const d = node && node.data;
    if (d && d.triggerType === "scheduled" && !d.cron && !d.runAt) {
      onChange({ ...d, cron: "0 9 * * *", timezone: d.timezone || "America/Sao_Paulo" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node && node.id]);

  const isIvrNode = !!(node && VERBS[node.data.verbKey] && VERBS[node.data.verbKey].isIvr);
  const ivrVendor = (node && node.data.ttsVendor) || "openai";

  // available providers (those with a key configured) — once per IVR node
  useEffect(() => {
    if (!isIvrNode) return;
    api.get("/tts/vendors").then(({ data }) => setVendors(data.vendors || ["openai"])).catch(() => setVendors(["openai"]));
  }, [isIvrNode]);

  // voices reload whenever the provider changes
  useEffect(() => {
    if (!isIvrNode) return;
    setVoices(null);
    api.get("/tts/voices", { params: { vendor: ivrVendor } })
      .then(({ data }) => setVoices(data.voices || []))
      .catch(() => setVoices([]));
  }, [isIvrNode, ivrVendor]);

  const previewVoice = async (voice, rate, vendor) => {
    try {
      setVoiceLoading(true);
      const text = (node && (node.data.prompt || node.data.sayText)) || "Olá, esta é uma demonstração da voz.";
      const { data } = await api.post("/tts/preview", {
        text,
        voice,
        vendor: vendor || ivrVendor,
        rate: rate || "normal",
      });
      if (data.url) audioToggle(data.url);
    } catch (err) {
      toastError(err);
    } finally {
      setVoiceLoading(false);
    }
  };
  const [codeModal, setCodeModal] = useState(null); // code field open in the big editor
  const [codeVarOpen, setCodeVarOpen] = useState(false); // var picker over the code editor
  const codeEditorRef = useRef(null);
  // "escrever com IA": instructions modal + streaming state + history viewer
  const [aiCodeOpen, setAiCodeOpen] = useState(false);
  const [aiInstr, setAiInstr] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const aiAbortRef = useRef(null);

  const isTrigger =
    !!node &&
    (node.data.verbKey === "__trigger__" || node.data.verbKey === "__start__");
  const nodeId = node && node.id;

  const loadEvents = async () => {
    if (!appId) return;
    try {
      const { data } = await api.get("/events", { params: { applicationId: appId } });
      setEvents((data.events || []).filter(isTriggerEvent));
    } catch (err) {
      toastError(err);
    }
  };

  // events for the trigger panel (real events, incl. tests) — auto-updating
  useEffect(() => {
    if (!isTrigger || !appId) {
      setEvents([]);
      return undefined;
    }
    loadEvents();
    const socket = connectToSocket();
    socket.emit("joinRoom", "events");
    socket.on("event", p => {
      if (
        p.action === "create" &&
        isTriggerEvent(p.event) &&
        String(p.event.applicationId) === String(appId)
      ) {
        setEvents(prev => [p.event, ...prev]);
      }
    });
    return () => {
      socket.emit("leaveRoom", "events");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, appId, isTrigger]);

  // configured connectors (for the "connector" field type)
  const loadConnectors = async () => {
    try {
      const { data } = await api.get("/connectors");
      setConnectors(data.connectors || []);
    } catch (err) {
      /* ignore */
    }
  };
  useEffect(() => {
    loadConnectors();
    (async () => {
      try {
        const { data } = await api.get("/numbers");
        setNumbers(data.numbers || []);
      } catch (err) {
        /* ignore */
      }
    })();
    // coletores do projeto (p/ o campo "collector" do gatilho "Coletor de dados")
    (async () => {
      try {
        const { data } = await api.get("/applications", { params: { projectId } });
        setCollectors((data.applications || []).filter(a => a.triggerType === "collector"));
        setApps(data.applications || []);
      } catch (err) {
        /* ignore */
      }
    })();
    // tabelas do projeto (p/ o campo "dataset" do nó "Salvar registro")
    (async () => {
      try {
        const { data } = await api.get("/datasets", { params: { projectId } });
        setDatasets(data.datasets || []);
      } catch (err) {
        /* ignore */
      }
    })();
  }, []);

  // carrega o ESQUEMA da tabela escolhido (para o mapeamento campo→valor)
  const selectedDatasetId = node && node.data && node.data.datasetId;
  useEffect(() => {
    const vdef = node && VERBS[node.data.verbKey];
    const needs = vdef && (vdef.fields || []).some(f => f.type === "recordMapping" || f.type === "recordFilters");
    if (!needs || !selectedDatasetId) {
      setDatasetSchema(null);
      return;
    }
    let alive = true;
    api
      .get(`/datasets/${selectedDatasetId}`)
      .then(({ data }) => {
        if (alive) setDatasetSchema(data.dataset || null);
      })
      .catch(() => alive && setDatasetSchema(null));
    return () => {
      alive = false;
    };
  }, [node && node.id, selectedDatasetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // carrega as ENTRADAS declaradas do agente escolhido (nó "Transferir para IA")
  const selectedAgentId = node && node.data && node.data.agentId;
  useEffect(() => {
    const vdef = node && VERBS[node.data.verbKey];
    const needs = vdef && (vdef.fields || []).some(f => f.type === "agentInputs");
    if (!needs || !selectedAgentId) {
      setAgentDeclaredInputs([]);
      return;
    }
    let alive = true;
    api
      .get(`/applications/${selectedAgentId}`)
      .then(({ data }) => {
        if (alive) setAgentDeclaredInputs(((data.triggerConfig || {}).inputs) || []);
      })
      .catch(() => alive && setAgentDeclaredInputs([]));
    return () => {
      alive = false;
    };
  }, [node && node.id, selectedAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // agendas do Google da CONTA escolhida (campo "gcalCalendar" dos nós do Google
  // Agenda). Recarrega quando a Conta (connectorId) muda. 404 se desconectada.
  const selectedGcalConnId = node && node.data && node.data.connectorId;
  useEffect(() => {
    const vdef = node && VERBS[node.data.verbKey];
    const needs = vdef && (vdef.fields || []).some(f => f.type === "gcalCalendar");
    if (!needs || !selectedGcalConnId) {
      setGcalCalendars([]);
      return;
    }
    let alive = true;
    api
      .get("/connectors/google/calendars", { params: { connectorId: selectedGcalConnId } })
      .then(({ data }) => {
        if (alive) setGcalCalendars(data.calendars || []);
      })
      .catch(() => alive && setGcalCalendars([]));
    return () => {
      alive = false;
    };
  }, [node && node.id, selectedGcalConnId]); // eslint-disable-line react-hooks/exhaustive-deps

  // sem nó selecionado o inspector fica MINIMIZADO (não renderiza) — o canvas ocupa
  // o espaço; ele abre ao selecionar um nó. (Multi-seleção é tratada no Builder.)
  if (!node) return null;

  const data = node.data;
  const set = (name, value) => onChange({ ...data, [name]: value });
  // "Captura exclusiva" (WhatsApp): se a conexão já é usada por outro fluxo ou por
  // um agente, avisa que eles deixam de receber e pede confirmação (STU-163)
  const confirmExclusive = async () => {
    try {
      if (data.connectorId) {
        const { data: u } = await api.get(`/connectors/${data.connectorId}/usage`, { params: { excludeAppId: appId || 0 } });
        const flows = (u && u.flows) || [];
        const agent = u && u.agent;
        if (flows.length || agent) {
          const quem = [
            flows.length ? `no${flows.length > 1 ? "s fluxos" : " fluxo"} ${flows.map(x => `"${x.name}"`).join(", ")}` : "",
            agent ? `no agente "${agent.name}"` : "",
          ].filter(Boolean).join(" e ");
          const otherExcl = flows.filter(x => x.exclusive).map(x => `"${x.name}"`);
          const ok = await confirm({
            title: "Marcar como exclusivo?",
            message:
              `Você está configurando um WhatsApp que já está sendo usado ${quem}. Marcar como exclusivo vai fazer com que ${flows.length + (agent ? 1 : 0) > 1 ? "eles deixem" : "ele deixe"} de funcionar para as mensagens desta conexão.` +
              (otherExcl.length ? ` ${otherExcl.join(", ")} também é exclusivo — vale o publicado por último.` : ""),
            confirmText: "Marcar como exclusivo",
            danger: true,
          });
          if (!ok) return;
        }
      }
    } catch (e) {
      /* sem a checagem, segue */
    }
    set("interceptExclusive", true);
  };

  // strip markdown code fences the model may wrap the answer in
  const stripFences = s => {
    let t = String(s || "");
    t = t.replace(/^\s*```[a-zA-Z]*\s*\n?/, "");
    t = t.replace(/\n?```\s*$/, "");
    return t;
  };
  // "Escrever com IA" for the code node: versions the previous code (if any),
  // then streams the generated code into the editor in real time.
  const runAiCode = async () => {
    const field = codeModal;
    if (!field || !aiInstr.trim() || aiBusy) return;
    const prev = String(data[field] || "");
    const histKey = `${field}History`;
    const prevHist = Array.isArray(data[histKey]) ? data[histKey] : [];
    // version the previous code BEFORE replacing it (only if there is one)
    const newHist = prev.trim()
      ? [{ code: prev, prompt: aiInstr, at: new Date().toISOString() }, ...prevHist].slice(0, 15)
      : prevHist;
    // frozen base kept across every streaming update (preserves the new history)
    const base = { ...data, [histKey]: newHist };
    setAiBusy(true);
    setAiErr(null);
    setAiCodeOpen(false); // fecha o modal de instruções p/ ver o editor escrevendo
    let acc = "";
    onChange({ ...base, [field]: "" }); // clear editor + commit the version
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const resp = await fetch("/api/ai/code/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: (api.defaults.headers || {}).Authorization || "",
        },
        body: JSON.stringify({ instructions: aiInstr, currentCode: prev, scope }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        let m = `Falha (${resp.status})`;
        try {
          const j = await resp.json();
          if (j && j.error) m = j.error;
        } catch (e) {
          /* keep status message */
        }
        throw new Error(m);
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { value: chunk, done } = await reader.read();
        if (done) break;
        buf += dec.decode(chunk, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const evt = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = evt.split("\n").find(l => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let msg;
          try {
            msg = JSON.parse(payload);
          } catch (e) {
            continue;
          }
          if (msg.delta) {
            acc += msg.delta;
            onChange({ ...base, [field]: acc });
          } else if (msg.error) {
            throw new Error(msg.error);
          }
        }
      }
      onChange({ ...base, [field]: stripFences(acc) });
      setAiCodeOpen(false);
    } catch (e) {
      if (e.name !== "AbortError") setAiErr(e.message || "Erro ao gerar código.");
    } finally {
      setAiBusy(false);
      aiAbortRef.current = null;
    }
  };
  const stopAiCode = () => {
    if (aiAbortRef.current) aiAbortRef.current.abort();
  };
  // restore a previous version (versions the current code first, so nothing is lost)
  const restoreCodeVersion = entry => {
    const field = codeModal;
    if (!field) return;
    const cur = String(data[field] || "");
    const histKey = `${field}History`;
    const hist = Array.isArray(data[histKey]) ? data[histKey] : [];
    const withCur = cur.trim() && cur !== entry.code
      ? [{ code: cur, prompt: "(antes de restaurar)", at: new Date().toISOString() }, ...hist].slice(0, 15)
      : hist;
    onChange({ ...data, [field]: entry.code, [histKey]: withCur });
    setHistOpen(false);
  };

  const confirmDelete = async () => {
    if (await confirm({ title: "Excluir nó", message: "Excluir este nó? Esta ação não pode ser desfeita.", confirmText: "Excluir", danger: true })) onDelete();
  };
  // enable/disable toggle (skipped at deploy/runtime when disabled)
  const def = isTrigger ? null : VERBS[data.verbKey];

  const hasVars = !!(
    scope &&
    scope.steps &&
    Object.values(scope.steps).some(
      s => s && typeof s === "object" && Object.keys(s).length
    )
  );
  const varsEnabled = !isTrigger && hasVars && !readOnly;

  const renderField = rawField => {
    // helperText pode ser FUNÇÃO de (data) quando o texto depende de outro campo
    // (ex.: no Coletar o significado do Timeout muda conforme o modo de encerrar).
    const f =
      typeof rawField.helperText === "function"
        ? { ...rawField, helperText: rawField.helperText(data) }
        : rawField;
    const value = data[f.name];
    // rótulo com o ícone de info ao lado (quando o campo declara `info`)
    const lbl = f.info ? (
      <>
        {f.label}
        <FieldInfo text={f.info} classes={classes} />
      </>
    ) : (
      f.label
    );
    const adorn = varsEnabled
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" tabIndex={-1} title="Inserir variável (dado de um passo anterior)" onClick={() => setVarField(f.name)}>
                <VarIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }
      : undefined;
    // valor = UM token só ({{steps…}})? então exibimos como CHIP legível (o valor
    // armazenado continua o token; é só render). "ver código" volta pro input cru.
    const singleTok = (() => {
      const s = String(value ?? "").trim();
      const m = /^\{\{\s*([^{}]+?)\s*\}\}$/.exec(s);
      return m ? m[1] : null;
    })();
    const asChip = !!singleTok;
    // chip DENTRO da caixa do input (lê-se como um valor de campo). O input real
    // fica com largura 0; o chip é a exibição. O único ícone é o raio (⚡): re-abre
    // o picker e SUBSTITUI a variável. Limpar = × no chip.
    const chipField = () => (
      <TextField
        key={f.name}
        className={classes.field}
        label={f.label}
        value=""
        fullWidth
        variant="outlined"
        size="small"
        helperText={f.helperText}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          className: classes.chipInput,
          startAdornment: (
            <VarChip inner={singleTok} groups={varGroups} onClear={readOnly ? undefined : () => set(f.name, "")} />
          ),
          endAdornment: !readOnly ? (
            <IconButton size="small" tabIndex={-1} className={classes.codeToggle} title="Escolher outro dado" onClick={() => setVarField(`set:${f.name}`)}>
              <VarIcon fontSize="small" />
            </IconButton>
          ) : undefined,
        }}
        inputProps={{ style: { width: 0, minWidth: 0, paddingLeft: 0, paddingRight: 0 }, tabIndex: -1, "aria-hidden": true }}
      />
    );
    switch (f.type) {
      case "callerid":
        return (
          <div key={f.name} className={classes.field}>
            <NumberSelect value={value} onChange={v => set(f.name, v)} label={f.label} disabled={readOnly} allowEmpty helperText={f.helperText} />
          </div>
        );
      case "route-agents":
        return (
          <div key={f.name} className={classes.field}>
            {f.label && <Typography className={classes.hint} style={{ marginBottom: 6 }}>{f.label}</Typography>}
            <RouteAgentEditor value={value} onChange={v => set(f.name, v)} projectId={projectId} disabled={readOnly} />
          </div>
        );
      case "agent-select":
        return (
          <div key={f.name} className={classes.field}>
            <AgentSelect
              value={value}
              label={f.label}
              projectId={projectId}
              disabled={readOnly}
              onChange={async v => {
                // cacheia os campos de saída do agente no nó (_outputs) para o
                // picker de variáveis sugerir steps.<nó>.<campo> nos nós seguintes
                let outs = [];
                try {
                  const { data: ag } = await api.get(`/applications/${v}`);
                  outs = (((ag.triggerConfig || {}).outputs) || []).map(o => o && o.name).filter(Boolean);
                } catch (e) {
                  /* ignore */
                }
                onChange({ ...data, [f.name]: v, _outputs: outs });
              }}
            />
          </div>
        );
      case "textarea":
        return (
          <div key={f.name} className={classes.field}>
            <TokenTextArea
              label={f.label}
              value={value || ""}
              onChange={v => set(f.name, v)}
              groups={varGroups}
              placeholder={f.placeholder}
              helperText={f.helperText}
              readOnly={readOnly}
              onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
            />
          </div>
        );
      case "openingMessages": {
        // igual ao textarea + prévia dos balões: cada bloco separado por LINHA EM
        // BRANCO (ou por uma linha de ___/---) vira um balão. Mesma separação do
        // backend (splitOpeningMessages) — deixa claro que quebrar linha = outra msg.
        const norm = String(value || "").replace(/^[ \t]*[_\-—–]{2,}[ \t]*$/gm, "");
        const blocks = norm.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
        return (
          <div key={f.name} className={classes.field}>
            <TokenTextArea
              label={f.label}
              value={value || ""}
              onChange={v => set(f.name, v)}
              groups={varGroups}
              placeholder={f.placeholder}
              helperText={f.helperText}
              readOnly={readOnly}
              onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
            />
            {blocks.length > 0 && (
              <div className={classes.bubblePrev}>
                <div className={classes.bubblePrevHead}>
                  {blocks.length === 1 ? "Prévia · 1 balão" : `Prévia · ${blocks.length} balões`}
                </div>
                {blocks.map((b, i) => (
                  <div key={i} className={classes.bubble}>{b}</div>
                ))}
                {blocks.length === 1 && (
                  <div className={classes.bubbleTip}>Pule uma linha em branco para enviar outro balão.</div>
                )}
              </div>
            )}
          </div>
        );
      }
      case "variable":
        // campo cujo valor é UMA variável (ex.: condicional). Texto livre + botão
        // que abre o picker e SUBSTITUI o valor pela variável escolhida ("set:").
        if (asChip) return chipField();
        return (
          <TextField
            key={f.name}
            className={classes.field}
            label={f.label}
            value={value || ""}
            onChange={e => set(f.name, e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            disabled={readOnly}
            placeholder={f.placeholder || "ex.: {{steps.coletar.digits}}"}
            helperText={f.helperText || "Escolha a variável a avaliar (clique no ícone)."}
            inputProps={trackCaret(f.name)}
            InputProps={
              readOnly
                ? undefined
                : {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" tabIndex={-1} title="Inserir variável (dado de um passo anterior)" onClick={() => setVarField(`set:${f.name}`)}>
                          <VarIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
            }
          />
        );
      // Attio: formulário com os campos REAIS do objeto no lugar do JSON. Lê e
      // escreve a mesma string que o campo `body` sempre guardou, então o
      // build() do nó não muda e nó antigo continua abrindo.
      case "attioValues":
        return (
          <AttioValues
            key={f.name}
            field={f}
            value={value}
            onChange={v => set(f.name, v)}
            nodeData={data}
            readOnly={readOnly}
            scope={scope}
            varGroups={varGroups}
            varRunAt={varRunAt}
            varsEnabled={varsEnabled}
          />
        );
      case "number":
        return (
          <TextField key={f.name} className={classes.field} label={lbl} type="number" value={value ?? ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText} inputProps={{ min: f.min, max: f.max }} />
        );
      case "stringlist": {
        const list = Array.isArray(value) ? value : [];
        const upd = (i, v) => { const next = list.slice(); next[i] = v; set(f.name, next); };
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            {list.map((opt, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <TextField value={opt || ""} onChange={e => upd(i, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} placeholder={`Opção ${i + 1}`} />
                {!readOnly && (
                  <IconButton size="small" tabIndex={-1} onClick={() => set(f.name, list.filter((_, k) => k !== i))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            ))}
            {!readOnly && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => set(f.name, [...list, ""])} style={{ textTransform: "none" }}>
                Adicionar opção
              </Button>
            )}
          </div>
        );
      }
      case "voice": {
        const projInherit = activeProject && activeProject.ttsVendor
          ? (activeProject.ttsVoice || "padrão do projeto")
          : "";
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint} style={{ marginBottom: 6 }}>{f.label}</Typography>
            <VoicePicker
              readOnly={readOnly}
              previewText={data.text}
              inheritLabel={projInherit}
              value={{ vendor: data.ttsVendor, voice: data.voice, rate: data.rate }}
              onChange={v => onChange({ ...data, ttsVendor: v.vendor, voice: v.voice, rate: v.rate })}
            />
          </div>
        );
      }
      case "code": {
        // raw JS — uses steps.* directly. The side panel is too narrow to write
        // code, so show a preview + open a big centered editor modal.
        const codeLines = String(value || "").split("\n");
        const codePreview = codeLines.slice(0, 6).join("\n");
        const histList = Array.isArray(data[`${f.name}History`]) ? data[`${f.name}History`] : [];
        const fmtHist = iso => {
          const d = new Date(iso);
          return isNaN(d.getTime()) ? "" : format(d, "dd/MM HH:mm");
        };
        return (
          <div key={f.name} className={classes.field}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
              <Typography className={classes.hint} style={{ flex: 1, marginBottom: 0 }}>{f.label}</Typography>
              <Button size="small" startIcon={<CodeIcon fontSize="small" />} onClick={() => setCodeModal(f.name)}>
                {readOnly ? "Ver código" : "Editar código"}
              </Button>
            </div>
            <div
              onClick={() => setCodeModal(f.name)}
              style={{ border: "1px solid #D3D1C7", borderRadius: 6, padding: 8, fontFamily: "monospace", fontSize: 11.5, color: "#5F5E5A", whiteSpace: "pre", overflow: "hidden", maxHeight: 130, cursor: "pointer", background: "#FAFAF7" }}
            >
              {codePreview || "// clique para escrever o código"}
              {codeLines.length > 6 ? `\n… (${codeLines.length} linhas)` : ""}
            </div>
            <Dialog open={codeModal === f.name} onClose={() => setCodeModal(null)} maxWidth="md" fullWidth>
              <DialogTitle style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ flex: 1 }}>Editar código{def ? ` — ${def.title}` : ""}</span>
                {!readOnly && (
                  <Button size="small" onClick={() => { setAiErr(null); setAiCodeOpen(true); }} style={{ textTransform: "none" }}>
                    <span role="img" aria-label="IA" style={{ marginRight: 4 }}>✨</span> Escrever com IA
                  </Button>
                )}
                {histList.length > 0 && (
                  <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={() => setHistOpen(true)}>
                    Versões ({histList.length})
                  </Button>
                )}
                {varsEnabled && (
                  <Button size="small" startIcon={<CodeIcon fontSize="small" />} onClick={() => setCodeVarOpen(true)}>
                    {i18n.t("vars.insert")}
                  </Button>
                )}
              </DialogTitle>
              <DialogContent>
                <Typography className={classes.hint} style={{ marginBottom: 8 }}>
                  Use <code>steps.*</code> (saída do gatilho e dos nós anteriores) e <code>return</code>.
                  Bibliotecas: <code>require(&apos;axios&apos;|&apos;dayjs&apos;|&apos;lodash&apos;)</code>.
                </Typography>
                <CodeEditor ref={codeEditorRef} value={value} onChange={v => set(f.name, v)} readOnly={readOnly || aiBusy} height="60vh" />
                {aiBusy && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, color: "#6b6b6b" }}>
                    <CircularProgress size={16} />
                    <span style={{ fontSize: 13, flex: 1 }}>Escrevendo código com IA…</span>
                    <Button size="small" startIcon={<StopIcon />} onClick={stopAiCode} color="secondary">Parar</Button>
                  </div>
                )}
                {aiErr && !aiCodeOpen && (
                  <Typography style={{ color: "#b00020", fontSize: 13, marginTop: 8 }}>{aiErr}</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCodeModal(null)} variant="contained" color="secondary">Concluir</Button>
              </DialogActions>
            </Dialog>

            {/* instruções para a IA */}
            <Dialog open={aiCodeOpen && codeModal === f.name} onClose={() => !aiBusy && setAiCodeOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle><span role="img" aria-label="IA">✨</span> Escrever código com IA</DialogTitle>
              <DialogContent>
                <Typography className={classes.hint} style={{ marginBottom: 8 }}>
                  Descreva o que o código deve fazer. {String(value || "").trim() ? "O código atual será versionado antes de ser substituído." : ""}
                </Typography>
                <TextField
                  autoFocus
                  multiline
                  minRows={4}
                  fullWidth
                  variant="outlined"
                  placeholder="Ex.: pegue steps.gatilho.body.cpf, consulte a API https://… via axios e retorne { nome, saldo }."
                  value={aiInstr}
                  onChange={e => setAiInstr(e.target.value)}
                  disabled={aiBusy}
                />
                {aiErr && <Typography style={{ color: "#b00020", fontSize: 13, marginTop: 8 }}>{aiErr}</Typography>}
              </DialogContent>
              <DialogActions>
                {aiBusy ? (
                  <Button onClick={stopAiCode} startIcon={<StopIcon />} color="secondary">Parar</Button>
                ) : (
                  <>
                    <Button onClick={() => setAiCodeOpen(false)}>Cancelar</Button>
                    <Button onClick={runAiCode} variant="contained" color="primary" disabled={!aiInstr.trim()}>
                      Gerar
                    </Button>
                  </>
                )}
              </DialogActions>
            </Dialog>

            {/* histórico de versões */}
            <Dialog open={histOpen && codeModal === f.name} onClose={() => setHistOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>Versões anteriores do código</DialogTitle>
              <DialogContent>
                {histList.length === 0 && <Typography className={classes.hint}>Nenhuma versão salva ainda.</Typography>}
                {histList.map((h, i) => (
                  <div key={i} style={{ border: "1px solid #E2E0D6", borderRadius: 6, padding: 8, marginBottom: 8, background: "#FAFAF7" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ flex: 1, fontSize: 12, color: "#888" }}>
                        {fmtHist(h.at)}{h.prompt ? ` · ${h.prompt}` : ""}
                      </span>
                      {!readOnly && (
                        <Button size="small" onClick={() => restoreCodeVersion(h)}>Restaurar</Button>
                      )}
                    </div>
                    <div style={{ fontFamily: "monospace", fontSize: 11.5, color: "#5F5E5A", whiteSpace: "pre", overflow: "hidden", maxHeight: 120 }}>
                      {String(h.code || "").split("\n").slice(0, 8).join("\n")}
                    </div>
                  </div>
                ))}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHistOpen(false)} variant="contained" color="secondary">Fechar</Button>
              </DialogActions>
            </Dialog>
            <VariablePicker
              open={codeVarOpen && codeModal === f.name}
              scope={scope}
              onClose={() => setCodeVarOpen(false)}
              onPick={path => {
                // code references vars as raw `steps.*` (not {{...}}), inserted at the caret
                if (codeEditorRef.current) codeEditorRef.current.insertAtCursor(path);
                setCodeVarOpen(false);
              }}
            />
          </div>
        );
      }
      case "select":
        // f.default cobre nós criados ANTES de o campo existir: os `defaults` do
        // verbo só são aplicados na criação, então sem isso o select abriria vazio.
        return (
          <TextField key={f.name} className={classes.field} label={lbl} select SelectProps={selectProps} value={value || f.default || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText}>
            {f.options.map(o => {
              const val = typeof o === "object" ? o.value : o;
              const lbl = typeof o === "object" ? o.label : o;
              return <MenuItem key={val} value={val}>{lbl}</MenuItem>;
            })}
          </TextField>
        );
      case "connector": {
        const opts = connectors.filter(c => c.type === f.connectorType);
        // conectores OAuth (ex.: Google) conectam na tela de Conectores, não por
        // um formulário de credencial — não ofereça "+ Nova conexão…" aqui.
        const cat = connByType(f.connectorType);
        const isOauth = !!(cat && cat.oauth);
        const onConn = e => {
          if (e.target.value === "__new__") {
            setNewConn({ type: f.connectorType, field: f.name });
          } else {
            set(f.name, e.target.value);
          }
        };
        const emptyHelper = isOauth ? "Nenhuma conta conectada — conecte em Conectores" : "Nenhuma conexão ainda — crie uma";
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={onConn} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText || (opts.length ? "Conexão usada por este nó" : emptyHelper)}>
            <MenuItem value="">{f.emptyLabel || "— selecione —"}</MenuItem>
            {opts.map(c => {
              const h = connectorHealth(c); // WhatsApp fora do ar aparece marcado (STU-176)
              return <MenuItem key={c.id} value={c.id}>{c.name}{h && h.alert ? ` — ${h.label}` : ""}</MenuItem>;
            })}
            {!isOauth && (
              <MenuItem value="__new__" style={{ fontWeight: 600 }}>+ Nova conexão…</MenuItem>
            )}
          </TextField>
        );
      }
      case "collector": {
        // seleciona qual Coletor de dados dispara esta automação (triggerConfig.collectorId)
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText || (collectors.length ? "A automação roda 1x por registro novo do coletor" : "Nenhum coletor ainda — crie um em Coletores")}>
            <MenuItem value="">— selecione —</MenuItem>
            {collectors.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
        );
      }
      case "codeJob": {
        // Script do projeto (STU-186): o script é publicado pela IA do usuário (MCP deploy_job)
        const jobs = apps.filter(a => a.triggerType === "code_job");
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly}
            helperText={jobs.length ? "Os scripts ficam em Integrations › Scripts." : "Nenhum script neste projeto — crie em Integrations › Scripts (ou pela sua IA, via MCP)."}>
            <MenuItem value="" disabled>Escolha o script</MenuItem>
            {jobs.map(a => (
              <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
            ))}
          </TextField>
        );
      }
      case "application": {
        // escolhe o FLUXO DE ORIGEM de um gatilho por evento de chamada (filtro).
        // Lista os fluxos do dono, menos o próprio (é o assinante, não a origem).
        const sourceApps = apps.filter(a => String(a.id) !== String(appId));
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText}>
            <MenuItem value="">— qualquer fluxo —</MenuItem>
            {sourceApps.map(a => (
              <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
            ))}
          </TextField>
        );
      }
      case "gcalCalendar": {
        // escolhe a agenda do Google (lista vinda de /connectors/google/calendars
        // da conta escolhida no campo "Conta"). Obrigatório escolher uma.
        const connChosen = !!(data && data.connectorId);
        if (!connChosen) {
          return (
            <TextField key={f.name} className={classes.field} label={f.label} value="" disabled fullWidth variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Escolha a Conta acima para listar as agendas." />
          );
        }
        if (!gcalCalendars.length) {
          return (
            <TextField key={f.name} className={classes.field} label={f.label} value="" disabled fullWidth variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Conecte a conta Google em Conectores para listar suas agendas." />
          );
        }
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText || "Escolha em qual agenda desta conta."}>
            <MenuItem value="">— selecione —</MenuItem>
            {gcalCalendars.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}{c.primary ? " (principal)" : ""}</MenuItem>
            ))}
          </TextField>
        );
      }
      case "dataset": {
        // escolhe a Tabela onde o nó "Salvar registro" acrescenta linhas
        return (
          <TextField key={f.name} className={classes.field} label={f.label} select SelectProps={{ ...selectProps, displayEmpty: true }} InputLabelProps={{ shrink: true }} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText || (datasets.length ? "Cada gravação vira um registro novo" : "Nenhuma tabela ainda — crie em Tabelas")}>
            <MenuItem value="">— selecione —</MenuItem>
            {datasets.map(dset => (
              <MenuItem key={dset.id} value={dset.id}>{dset.name}</MenuItem>
            ))}
          </TextField>
        );
      }
      case "recordMapping": {
        // uma entrada por campo declarado da tabela escolhido (aceita {{variáveis}})
        if (!selectedDatasetId) {
          return <Typography key={f.name} className={classes.hint}>Escolha uma tabela acima para mapear os campos.</Typography>;
        }
        const schemaFields = (datasetSchema && datasetSchema.fields) || [];
        if (!schemaFields.length) {
          return <Typography key={f.name} className={classes.hint}>Esta tabela ainda não tem campos declarados.</Typography>;
        }
        const values = data.values && typeof data.values === "object" ? data.values : {};
        const setVal = (fname, v) => set("values", { ...values, [fname]: v });
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            {schemaFields.map(sf => (
              <div key={sf.name} className={classes.field}>
                <TokenTextArea
                  singleLine
                  label={`${sf.label || sf.name} · ${sf.type}`}
                  value={values[sf.name] || ""}
                  onChange={v => setVal(sf.name, v)}
                  groups={varGroups}
                  placeholder="valor fixo ou variável"
                  helperText={sf.description || undefined}
                  readOnly={readOnly}
                  onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
                />
              </div>
            ))}
          </div>
        );
      }
      case "recordFilters": {
        // "Buscar registros": filtros campo / operador / valor (aceita {{variáveis}})
        if (!selectedDatasetId) {
          return <Typography key={f.name} className={classes.hint}>Escolha uma tabela acima para filtrar.</Typography>;
        }
        const schemaFields = (datasetSchema && datasetSchema.fields) || [];
        const filters = Array.isArray(data.filters) ? data.filters : [];
        const setFilters = next => set("filters", next);
        const OPS = [
          { value: "=", label: "é igual a" },
          { value: "≠", label: "é diferente de" },
          { value: "contém", label: "contém" },
          { value: ">", label: "maior que" },
          { value: "<", label: "menor que" },
          { value: "vazio", label: "está vazio" },
          { value: "notempty", label: "não está vazio" },
        ];
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label} — sem filtro, traz os mais recentes</Typography>
            {filters.map((flt, i) => {
              const upd = patch => setFilters(filters.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              const noValue = flt.op === "vazio" || flt.op === "notempty";
              return (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 8 }}>
                  <TextField select size="small" variant="outlined" label="Campo" value={flt.campo || ""} onChange={e => upd({ campo: e.target.value })} disabled={readOnly} style={{ width: 120 }}>
                    {schemaFields.map(sf => <MenuItem key={sf.name} value={sf.name}>{sf.label || sf.name}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" variant="outlined" label="Condição" value={flt.op || "="} onChange={e => upd({ op: e.target.value })} disabled={readOnly} style={{ width: 130 }}>
                    {OPS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  {!noValue && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TokenTextArea
                        singleLine
                        label="Valor"
                        value={flt.valor || ""}
                        onChange={v => upd({ valor: v })}
                        groups={varGroups}
                        placeholder="valor ou variável"
                        readOnly={readOnly}
                        onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
                      />
                    </div>
                  )}
                  {!readOnly && (
                    <IconButton size="small" onClick={() => setFilters(filters.filter((_, j) => j !== i))} title="Remover filtro">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              );
            })}
            {!readOnly && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => setFilters([...filters, { campo: (schemaFields[0] && schemaFields[0].name) || "", op: "=", valor: "" }])} style={{ textTransform: "none" }}>
                Adicionar filtro
              </Button>
            )}
          </div>
        );
      }
      case "agentInputs": {
        // um campo por ENTRADA declarada do agente escolhido (aceita {{variáveis}})
        if (!selectedAgentId) {
          return <Typography key={f.name} className={classes.hint}>Escolha o agente acima para informar os dados.</Typography>;
        }
        if (!agentDeclaredInputs.length) {
          return <Typography key={f.name} className={classes.hint}>Este agente não declara entradas. Declare em Agentes → o agente → Entradas.</Typography>;
        }
        const values = data.inputs && typeof data.inputs === "object" ? data.inputs : {};
        const setVal = (name, v) => set("inputs", { ...values, [name]: v });
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            {agentDeclaredInputs.map(inp => (
              <div key={inp.name} className={classes.field}>
                <TokenTextArea
                  singleLine
                  label={inp.description ? `${inp.name} · ${inp.description}` : inp.name}
                  value={values[inp.name] || ""}
                  onChange={v => setVal(inp.name, v)}
                  groups={varGroups}
                  placeholder="valor fixo ou variável"
                  readOnly={readOnly}
                  onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
                />
              </div>
            ))}
          </div>
        );
      }
      case "phone": {
        const sel = numbers.find(n => String(n.id) === String(value));
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            <Button
              variant="outlined"
              fullWidth
              disabled={readOnly}
              style={{ justifyContent: "flex-start", textTransform: "none" }}
              onClick={() => setNumberPicker(f.name)}
            >
              {String(value) === "any"
                ? "Qualquer número"
                : sel
                ? `${formatPhone(sel.number)}${sel.city ? ` — ${sel.city}` : ""}`
                : "Escolher número…"}
            </Button>
            <NumberPickerModal
              open={numberPicker === f.name}
              numbers={numbers}
              value={value}
              currentAppId={appId}
              onClose={() => setNumberPicker(null)}
              onPick={id => { set(f.name, id); setNumberPicker(null); }}
            />
          </div>
        );
      }
      // Escolha exclusiva entre poucas opções, em botões segmentados. Use com
      // showIf nos campos que dependem da escolha (ex.: Falar -> texto |
      // Tocar áudio -> mp3). `exclusive` + guarda de null: clicar no que já está
      // selecionado não pode deixar o campo sem valor.
      case "toggle": {
        const atual = value || f.default || (f.options[0] && (f.options[0].value ?? f.options[0]));
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{lbl}</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={atual}
              onChange={(e, v) => { if (v != null) set(f.name, v); }}
            >
              {f.options.map(o => {
                const val = typeof o === "object" ? o.value : o;
                const lbl = typeof o === "object" ? o.label : o;
                return (
                  <ToggleButton key={val} value={val} disabled={readOnly} style={{ textTransform: "none" }}>
                    {lbl}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            {f.helperText && <Typography className={classes.hint} style={{ marginTop: 4 }}>{f.helperText}</Typography>}
          </div>
        );
      }
      case "boolean":
        return (
          <div key={f.name} className={classes.field}>
            <FormControlLabel control={<Switch checked={!!value} onChange={e => (f.name === "interceptExclusive" && e.target.checked ? confirmExclusive() : set(f.name, e.target.checked))} color="primary" disabled={readOnly} />} label={lbl} />
            {f.helperText && <Typography className={classes.hint}>{f.helperText}</Typography>}
          </div>
        );
      case "multiselect":
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{lbl}</Typography>
            {f.options.map(o => {
              const val = typeof o === "object" ? o.value : o;
              const optLbl = typeof o === "object" ? o.label : o;
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.indexOf(val) >= 0;
              return (
                <FormControlLabel key={val} control={<Checkbox checked={checked} onChange={e => { const next = e.target.checked ? [...arr, val] : arr.filter(x => x !== val); set(f.name, next); }} color="primary" disabled={readOnly} />} label={optLbl} />
              );
            })}
          </div>
        );
      case "ivrMenu":
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint} style={{ flex: 1 }}>{f.label}</Typography>
            <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setMenuModal(true)} disabled={readOnly} variant="outlined">
              {readOnly ? "Ver mensagem e voz" : "Editar mensagem e voz"}
            </Button>
            <div
              onClick={() => setMenuModal(true)}
              style={{ marginTop: 6, border: "1px solid #D3D1C7", borderRadius: 6, padding: 8, fontSize: 12, color: "#5F5E5A", cursor: "pointer", background: "#FAFAF7", maxHeight: 70, overflow: "hidden" }}
            >
              {data.prompt || "Clique para escrever a mensagem do menu"}
            </div>
          </div>
        );
      case "schedule": {
        const TZ = [
          { value: "America/Sao_Paulo", label: "Brasília", gmt: "GMT-3" },
          { value: "America/Manaus", label: "Manaus", gmt: "GMT-4" },
          { value: "America/Cuiaba", label: "Cuiabá", gmt: "GMT-4" },
          { value: "America/Belem", label: "Belém", gmt: "GMT-3" },
          { value: "America/Fortaleza", label: "Fortaleza", gmt: "GMT-3" },
          { value: "America/Recife", label: "Recife", gmt: "GMT-3" },
          { value: "America/Rio_Branco", label: "Rio Branco", gmt: "GMT-5" },
          { value: "America/Noronha", label: "F. de Noronha", gmt: "GMT-2" },
          { value: "UTC", label: "UTC", gmt: "GMT+0" },
        ];
        const DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
        const num = s => /^\d+$/.test(s);
        const parse = cron => {
          const p = String(cron || "").trim().split(/\s+/);
          if (p.length !== 5) return { type: "daily", time: "09:00", days: [1, 2, 3, 4, 5], dom: 1 };
          const [m, h, dom, mon, dow] = p;
          if (!num(m) || !num(h)) return { type: "cron", time: "09:00", days: [1, 2, 3, 4, 5], dom: 1 };
          const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
          if (dow !== "*" && dom === "*" && mon === "*")
            return { type: "weekly", time, days: dow.split(",").map(Number).filter(n => !isNaN(n)), dom: 1 };
          if (dom !== "*" && dow === "*" && mon === "*" && num(dom))
            return { type: "monthly", time, days: [1, 2, 3, 4, 5], dom: Number(dom) };
          if (dom === "*" && dow === "*" && mon === "*")
            return { type: "daily", time, days: [1, 2, 3, 4, 5], dom: 1 };
          return { type: "cron", time: "09:00", days: [1, 2, 3, 4, 5], dom: 1 };
        };
        const cur = parse(data.cron);
        const pad2 = x => String(x).padStart(2, "0");
        const build = (type, time, days, dom) => {
          const [h, m] = (time || "09:00").split(":").map(x => parseInt(x, 10) || 0);
          if (type === "weekly") return `${m} ${h} * * ${days && days.length ? days.join(",") : "*"}`;
          if (type === "monthly") return `${m} ${h} ${dom || 1} * *`;
          return `${m} ${h} * * *`;
        };
        // múltiplos horários por dia -> 1 cron por horário (data.crons; data.cron
        // guarda o 1º p/ compat. + aba avançada). Apps legados (só data.cron, sem
        // data.times) caem no horário único parseado.
        // mantém a ORDEM de digitação (não ordenar aqui, senão o campo editado
        // "pula" de posição e parece alterar os outros). Ordeno só na exibição.
        const times =
          Array.isArray(data.times) && data.times.length
            ? data.times.slice()
            : [cur.time];
        const commit = (type, timesArr, days, dom) => {
          const t = type === "cron" ? "daily" : type;
          const list = (timesArr || []).filter(Boolean);
          const final = list.length ? list : ["09:00"];
          // duplicados são tolerados na UI; dedup só nos crons enviados ao backend
          const crons = Array.from(new Set(final.map(x => build(t, x, days, dom))));
          onChange({ ...data, runAt: undefined, times: final, crons, cron: crons[0] });
        };
        const replaceTime = (idx, v) => {
          const arr = times.slice();
          arr[idx] = v || "09:00";
          commit(cur.type, arr, cur.days, cur.dom);
        };
        const appendTime = () => {
          const last = times[times.length - 1] || "09:00";
          let [h, m] = last.split(":").map(x => parseInt(x, 10) || 0);
          h = (h + 1) % 24;
          commit(cur.type, [...times, `${pad2(h)}:${pad2(m)}`], cur.days, cur.dom);
        };
        const removeTime = idx => {
          const left = times.filter((_, i) => i !== idx);
          commit(cur.type, left.length ? left : ["09:00"], cur.days, cur.dom);
        };
        const toggleDay = i => {
          const days = cur.days.includes(i) ? cur.days.filter(x => x !== i) : [...cur.days, i].sort();
          commit("weekly", times, days, cur.dom);
        };
        const setDom = v => commit("monthly", times, cur.days, Math.min(31, Math.max(1, parseInt(v, 10) || 1)));
        const DOW1 = ["D", "S", "T", "Q", "Q", "S", "S"];
        const FULL = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
        const tz = data.timezone || "America/Sao_Paulo";
        const tzInfo = TZ.find(t => t.value === tz) || TZ[0];
        const p2 = x => String(x).padStart(2, "0");
        // GMT-3 -> "-03:00" (used to make the one-time runAt timezone-explicit)
        const offsetOf = gmt => {
          const m = /GMT([+-]?\d+)/.exec(gmt || "");
          const h = m ? parseInt(m[1], 10) : 0;
          return `${h < 0 ? "-" : "+"}${p2(Math.abs(h))}:00`;
        };
        const isOnce = !!data.runAt;
        const freq = isOnce ? "once" : cur.type;
        const runAtLocal = data.runAt ? String(data.runAt).slice(0, 16) : "";
        const defaultRunAt = () => {
          const d = new Date();
          d.setHours(d.getHours() + 1, 0, 0, 0); // próxima hora cheia (hoje)
          return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:00`;
        };
        const setFreq = f => {
          if (f === "once") {
            onChange({ ...data, runAt: `${defaultRunAt()}:00${offsetOf(tzInfo.gmt)}`, cron: undefined, crons: undefined, times: undefined, schedMode: "visual" });
          } else {
            commit(f, times, cur.days, cur.dom);
          }
        };
        const setRunAt = v => onChange({ ...data, runAt: v ? `${v}:00${offsetOf(tzInfo.gmt)}` : undefined });
        // mode is an explicit flag (a valid cron like "0 9 * * *" parses as a
        // known pattern, so we can't derive "advanced" from the cron alone).
        const advanced = data.schedMode === "advanced";
        const setMode = adv => {
          if (adv) onChange({ ...data, schedMode: "advanced", runAt: undefined, cron: data.cron || build("daily", times[0]) });
          else onChange({ ...data, schedMode: "visual", cron: cur.type === "cron" ? "0 9 * * *" : data.cron });
        };
        // timezone change: keep the one-time instant's wall-clock, fix the offset
        const setTz = v => {
          const info = TZ.find(t => t.value === v) || TZ[0];
          if (isOnce && data.runAt) onChange({ ...data, timezone: v, runAt: `${runAtLocal}:00${offsetOf(info.gmt)}` });
          else set("timezone", v);
        };
        const human = () => {
          if (isOnce) {
            const d = new Date(data.runAt);
            return isNaN(d.getTime())
              ? "Data inválida"
              : `Uma vez em ${["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][d.getDay()]}, ${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()} às ${p2(d.getHours())}:${p2(d.getMinutes())}`;
          }
          const ts = times.slice().sort().join(", ");
          if (cur.type === "daily") return `Todo dia às ${ts}`;
          if (cur.type === "weekly") {
            if (!cur.days.length) return "Selecione ao menos um dia";
            const when =
              cur.days.length === 7
                ? "Todo dia"
                : cur.days.length === 1
                ? `Toda ${FULL[cur.days[0]]}`
                : cur.days.map(i => DOW[i]).join(", ");
            return `${when} às ${ts}`;
          }
          if (cur.type === "monthly") return `Todo dia ${cur.dom} às ${ts}`;
          return "Agendamento personalizado";
        };
        const nextRuns = n => {
          if (advanced || isOnce || (cur.type === "weekly" && !cur.days.length)) return [];
          const now = new Date();
          const out = [];
          for (let i = 0; i < 400 && out.length < n; i++) {
            const base = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
            const dayOk =
              cur.type === "daily" ? true : cur.type === "weekly" ? cur.days.includes(base.getDay()) : base.getDate() === cur.dom;
            if (!dayOk) continue;
            for (const t of times) {
              const [hh, mm] = t.split(":").map(x => parseInt(x, 10) || 0);
              const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm, 0, 0);
              if (d > now) out.push(d);
            }
          }
          out.sort((a, b) => a - b);
          return out.slice(0, n);
        };
        const fmtRun = d => {
          const wd = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][d.getDay()];
          const p2 = x => String(x).padStart(2, "0");
          return `${wd}, ${p2(d.getDate())}/${p2(d.getMonth() + 1)} · ${p2(d.getHours())}:${p2(d.getMinutes())}`;
        };
        const runs = nextRuns(3);
        const segBtn = (active, label, onClick) => (
          <Button
            key={label}
            size="small"
            disableElevation
            onClick={onClick}
            variant={active ? "contained" : "text"}
            color={active ? "primary" : "default"}
            style={{ flex: 1, textTransform: "none", boxShadow: "none", fontWeight: active ? 700 : 500 }}
          >
            {label}
          </Button>
        );
        return (
          <div key={f.name} className={classes.field}>
            <div style={{ display: "flex", gap: 4, background: "#eceef0", borderRadius: 8, padding: 3, marginBottom: 12 }}>
              {segBtn(!advanced, "Visual", () => setMode(false))}
              {segBtn(advanced, "Cron avançado", () => setMode(true))}
            </div>

            {advanced ? (
              <TextField className={classes.field} label="Cron" placeholder="0 9 * * *" value={data.cron || ""} onChange={e => set("cron", e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText="min hora dia mês dia-da-semana" />
            ) : (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <TextField select SelectProps={selectProps} label="Frequência" value={freq} onChange={e => setFreq(e.target.value)} variant="outlined" size="small" disabled={readOnly} style={{ flex: 1 }} fullWidth>
                    <MenuItem value="once">Uma vez</MenuItem>
                    <MenuItem value="daily">Todos os dias</MenuItem>
                    <MenuItem value="weekly">Toda semana</MenuItem>
                    <MenuItem value="monthly">Todo mês</MenuItem>
                  </TextField>
                </div>

                {!isOnce && (
                  <div style={{ marginTop: 10 }}>
                    <Typography className={classes.hint} style={{ marginBottom: 4 }}>
                      Horários {times.length > 1 ? `(${times.length}× ao dia)` : ""}
                    </Typography>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {times.map((t, i) => (
                        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <TextField type="time" value={t} onChange={e => replaceTime(i, e.target.value)} variant="outlined" size="small" disabled={readOnly} InputLabelProps={{ shrink: true }} style={{ width: 130 }} />
                          {times.length > 1 && !readOnly && (
                            <Button size="small" onClick={() => removeTime(i)} style={{ minWidth: 0, padding: "2px 8px", color: "#b00" }}>✕</Button>
                          )}
                        </div>
                      ))}
                    </div>
                    {!readOnly && (
                      <Button size="small" onClick={appendTime} style={{ textTransform: "none", marginTop: 4, padding: 0 }} color="primary">
                        + Adicionar horário
                      </Button>
                    )}
                  </div>
                )}

                {isOnce && (
                  <TextField className={classes.field} label="Data e hora" type="datetime-local" value={runAtLocal} onChange={e => setRunAt(e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} InputLabelProps={{ shrink: true }} style={{ marginTop: 10 }} />
                )}

                {!isOnce && cur.type === "weekly" && (
                  <div style={{ marginTop: 10 }}>
                    <Typography className={classes.hint} style={{ marginBottom: 4 }}>Dias da semana</Typography>
                    <div style={{ display: "flex", gap: 4 }}>
                      {DOW1.map((d, i) => (
                        <Button key={i} size="small" onClick={() => toggleDay(i)} disabled={readOnly} variant={cur.days.includes(i) ? "contained" : "outlined"} color={cur.days.includes(i) ? "primary" : "default"} style={{ minWidth: 0, flex: 1, padding: "5px 0" }}>
                          {d}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                {cur.type === "monthly" && (
                  <TextField className={classes.field} label="Dia do mês" type="number" value={cur.dom} onChange={e => setDom(e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} inputProps={{ min: 1, max: 31 }} style={{ marginTop: 10 }} />
                )}

                <div style={{ background: "#f3f4f1", borderRadius: 8, padding: "10px 12px", marginTop: 12 }}>
                  <div style={{ fontSize: 14, color: "#222", marginBottom: runs.length ? 6 : 0 }}>{human()}</div>
                  {runs.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: "#999", marginBottom: 3 }}>Próximas execuções ({tzInfo.gmt})</div>
                      {runs.map((r, i) => (
                        <div key={i} style={{ fontSize: 13, color: "#555" }}>• {fmtRun(r)}</div>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}

            <TextField select SelectProps={selectProps} className={classes.field} label="Fuso horário" value={tz} onChange={e => setTz(e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} style={{ marginTop: 12 }}>
              {TZ.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label} · {t.gmt}</MenuItem>
              ))}
            </TextField>
          </div>
        );
      }
      case "whatsappTemplate": {
        const varCount = body => {
          const nums = (String(body || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(s =>
            parseInt(s.replace(/\D/g, ""), 10)
          );
          return nums.length ? Math.max(...nums) : 0;
        };
        const n = varCount(data.templateBody);
        const params = Array.isArray(data.params) ? data.params : [];
        const onSelectTpl = tid => {
          const t = waTpls.find(x => x.id === tid);
          const vn = varCount(t && t.data);
          onChange({
            ...data,
            templateId: t ? t.id : "",
            templateName: t ? t.elementName : "",
            templateBody: t ? t.data : "",
            params: Array.from({ length: vn }, (_, i) => params[i] || ""),
          });
        };
        const setParam = (i, v) => {
          const arr = [...params];
          arr[i] = v;
          set("params", arr);
        };
        // o detalhe do erro já vem da Gupshup em r.body (ex.: {"message":"Invalid
        // Destination"}); antes a UI mostrava só o HTTP e qualquer falha ficava opaca.
        const gupshupError = body => {
          try {
            const parsed = typeof body === "string" ? JSON.parse(body) : body;
            return (parsed && (parsed.message || parsed.error)) || "";
          } catch (e) {
            return typeof body === "string" ? body.slice(0, 120) : "";
          }
        };
        const runTest = async () => {
          setWaTesting(true);
          setWaTestMsg(null);
          try {
            const { data: r } = await api.post("/connectors/gupshup/send-test", {
              connectorId: data.connectorId,
              destination: waTestTo,
              templateId: data.templateId,
              params,
            });
            if (r.ok) {
              setWaTestMsg({ ok: true, text: "✓ Enviado!" });
            } else {
              const detail = gupshupError(r.body);
              setWaTestMsg({ ok: false, text: detail ? `✕ ${detail} (HTTP ${r.status})` : `✕ Falhou (HTTP ${r.status})` });
            }
          } catch (e) {
            // erro do nosso backend (AppError) → { error: "mensagem" }
            const detail = (e && e.response && e.response.data && e.response.data.error) || "";
            setWaTestMsg({ ok: false, text: detail ? `✕ ${detail}` : "✕ Erro ao enviar (número ativo?)" });
          } finally {
            setWaTesting(false);
          }
        };
        return (
          <div key={f.name} className={classes.field}>
            {!data.connectorId ? (
              <Typography className={classes.hint}>Selecione o conector acima primeiro.</Typography>
            ) : waTplLoading ? (
              <Typography className={classes.hint}>Carregando templates…</Typography>
            ) : (
              <>
                <TextField
                  className={classes.field}
                  label="Template (aprovados)"
                  select
                  SelectProps={selectProps}
                  value={data.templateId || ""}
                  onChange={e => onSelectTpl(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={readOnly}
                  helperText={waTpls.length ? "Só templates aprovados pela Meta" : "Nenhum template aprovado ainda"}
                >
                  <MenuItem value="">— selecione —</MenuItem>
                  {waTpls.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.elementName} ({t.languageCode})
                    </MenuItem>
                  ))}
                </TextField>
                {data.templateBody && (
                  <Typography variant="caption" style={{ display: "block", color: "#666", margin: "2px 0 8px", whiteSpace: "pre-wrap" }}>
                    {data.templateBody}
                  </Typography>
                )}
                {n === 0 && data.templateId && (
                  <Typography variant="caption" style={{ color: "#888" }}>
                    Este template não tem variáveis.
                  </Typography>
                )}
                {Array.from({ length: n }).map((_, i) => {
                  const pv = String(params[i] || "");
                  const tok = /^\{\{\s*([^{}]+?)\s*\}\}$/.exec(pv.trim());
                  const lbl = `Variável {{${i + 1}}}`;
                  // valor = um dado do fluxo (token único) → chip legível na caixa
                  if (tok) {
                    return (
                      <TextField
                        key={i}
                        className={classes.field}
                        label={lbl}
                        value=""
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                          className: classes.chipInput,
                          startAdornment: <VarChip inner={tok[1]} groups={varGroups} onClear={readOnly ? undefined : () => setParam(i, "")} />,
                          endAdornment: !readOnly ? (
                            <IconButton size="small" tabIndex={-1} className={classes.codeToggle} title="Escolher outro dado" onClick={() => setVarField(`param:${i}`)}>
                              <VarIcon fontSize="small" />
                            </IconButton>
                          ) : undefined,
                        }}
                        inputProps={{ style: { width: 0, minWidth: 0, paddingLeft: 0, paddingRight: 0 }, tabIndex: -1, "aria-hidden": true }}
                      />
                    );
                  }
                  return (
                    <TextField
                      key={i}
                      className={classes.field}
                      label={lbl}
                      placeholder="texto fixo ou um dado do fluxo"
                      value={pv}
                      onChange={e => setParam(i, e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={readOnly}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" tabIndex={-1} title="Inserir um dado do fluxo" onClick={() => setVarField(`param:${i}`)}>
                              <VarIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                })}
                {data.templateId && (
                  <>
                    <TextField
                      className={classes.field}
                      label="Número de teste"
                      placeholder="31999999999"
                      value={waTestTo}
                      onChange={e => setWaTestTo(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      helperText="Só para o teste abaixo — precisa ser um número com WhatsApp ativo. Não altera o destino do nó."
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                      <Button variant="outlined" size="small" onClick={runTest} disabled={waTesting || waTestTo.replace(/\D/g, "").length < 10}>
                        {waTesting ? "Enviando…" : "Testar envio"}
                      </Button>
                      {waTestMsg && (
                        <Typography variant="body2" style={{ color: waTestMsg.ok ? "#1E7E34" : "#B12A2A", fontWeight: 600 }}>
                          {waTestMsg.text}
                        </Typography>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      }
      case "extensions": {
        const sel = Array.isArray(value) ? value : [];
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            {extList.length === 0 ? (
              <Typography className={classes.hint}>Nenhum ramal cadastrado (crie na tela Ramais).</Typography>
            ) : (
              extList.map(e => {
                const checked = sel.indexOf(e.sipUsername) >= 0;
                return (
                  <FormControlLabel
                    key={e.id}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={ev => {
                          const next = ev.target.checked ? [...sel, e.sipUsername] : sel.filter(x => x !== e.sipUsername);
                          set(f.name, next);
                        }}
                        color="primary"
                        disabled={readOnly}
                      />
                    }
                    label={`${e.sipUsername} — ${e.name}`}
                  />
                );
              })
            )}
          </div>
        );
      }
      case "audio":
        return (
          <div key={f.name} className={classes.field}>
            <Typography className={classes.hint}>{f.label}</Typography>
            <Button
              variant="outlined"
              fullWidth
              disabled={readOnly}
              style={{ justifyContent: "flex-start", textTransform: "none" }}
              startIcon={<MusicIcon fontSize="small" />}
              onClick={() => setAudioField(f.name)}
            >
              {value ? "Trocar música / áudio…" : "Escolher música / áudio…"}
            </Button>
            {value && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                <Typography noWrap style={{ flex: 1, fontSize: 11, fontFamily: "monospace", color: "#5F5E5A" }}>
                  {value}
                </Typography>
                <IconButton size="small" title={audioPlaying === value ? "Parar" : "Ouvir"} onClick={() => audioToggle(value)}>
                  {audioPlaying === value ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
                {!readOnly && (
                  <IconButton size="small" title="Remover" onClick={() => set(f.name, "")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            )}
            {f.helperText && <Typography className={classes.hint} style={{ marginTop: 4 }}>{f.helperText}</Typography>}
          </div>
        );
      default: {
        if (asChip) return chipField();
        // texto MISTO (texto + token) num campo que aceita variáveis: editor inline
        const hasToken = /\{\{\s*[^{}]+?\s*\}\}/.test(String(value || ""));
        if (hasToken && varsEnabled) {
          return (
            <div key={f.name} className={classes.field}>
              <TokenTextArea
                singleLine
                label={f.label}
                value={value || ""}
                onChange={v => set(f.name, v)}
                groups={varGroups}
                placeholder={f.placeholder}
                helperText={f.helperText}
                readOnly={readOnly}
                onOpenPicker={insertFn => { areaInsert.current = insertFn; setVarField("area"); }}
              />
            </div>
          );
        }
        return (
          <TextField key={f.name} className={classes.field} label={lbl} placeholder={f.placeholder} value={value || ""} onChange={e => set(f.name, e.target.value)} fullWidth variant="outlined" size="small" disabled={readOnly} helperText={f.helperText} InputProps={adorn} inputProps={trackCaret(f.name)} />
        );
      }
    }
  };

  const dotClass = s => (s === "success" ? classes.dotOk : s === "error" ? classes.dotErr : classes.dotInfo);

  // the trigger event's runtime shape lives in data.output.event
  // ({method,query,headers,body,...}) — that's what the flow scope exposes as
  // steps.trigger.event. Fall back to the raw data for older/odd events.
  const sampleEventOf = d => (d && d.output && d.output.event) || d;

  const handleSent = async () => {
    // NÃO fecha o modal: ele mostra o Resultado/resposta do teste (o usuário fecha
    // no "Fechar"). Só atualiza a lista de eventos + o sample por baixo.
    try {
      const { data } = await api.get("/events", { params: { applicationId: appId } });
      const trigs = (data.events || []).filter(isTriggerEvent);
      setEvents(trigs);
      if (trigs[0]) {
        onSetSample({ event: sampleEventOf(trigs[0].data) });
        setSelectedEventId(trigs[0].id);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const selectEvent = ev => {
    onSetSample({ event: sampleEventOf(ev.data) });
    setSelectedEventId(ev.id);
  };

  // ---- TRIGGER NODE: collapsible sections ----
  if (isTrigger) {
    const trig = data.triggerType ? TRIGGERS[data.triggerType] : null;

    if (!trig) {
      return (
        <div className={classes.root}>
          <Typography className={classes.title}>{i18n.t("triggers.title")}</Typography>
          <Typography className={classes.hint}>{i18n.t("triggers.empty")}</Typography>
          {!readOnly && (
            <Button variant="contained" color="secondary" onClick={onPickTrigger}>{i18n.t("triggers.add")}</Button>
          )}
        </div>
      );
    }

    // ── Ferramenta para IA: casca padrão (header + abas + rodapé) ──────────
    if (data.triggerType === "tool") {
      const paramsSample = { input: toolSampleOf(data.inputParams) };
      return (
        <NodeConfigShell
          tab={shellTab}
          onTabChange={setShellTab}
          icon={<NodeIcon triggerType="tool" color={trig.color} size={34} chip />}
          name={data.label || trig.title}
          nameEditable={!readOnly}
          onName={v => set("label", v)}
          tag="gatilho"
          subtitle="Uma função que a IA pode chamar durante a conversa. Você define os parâmetros que ela preenche; o fluxo processa e devolve um valor."
          onClose={onClose}
          footer={
            onTestTool ? (
              <Button fullWidth variant="contained" disableElevation startIcon={<PlayArrowIcon />} onClick={onTestTool}
                style={{ background: "#2C353D", color: "#fff", textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
                Testar ferramenta
              </Button>
            ) : null
          }
          config={
            <div>
              <Typography className={classes.leadText}>
                Defina parâmetros que a IA deve ou pode enviar para serem usados nos próximos passos.
              </Typography>
              <ToolInputEditor
                value={data.inputParams}
                readOnly={readOnly}
                onChange={params => onChange({ ...data, inputParams: params, sample: toolSampleOf(params) })}
              />
            </div>
          }
          test={
            <div>
              <Typography className={classes.leadText}>
                Preencha valores de teste e execute (botão abaixo). O retorno aparece na janela de teste e
                as variáveis dos passos ficam disponíveis no seletor de variáveis do fluxo.
              </Typography>
              <Typography className={classes.hint} style={{ marginTop: 4 }}>Variáveis desta ferramenta</Typography>
              <JsonTree data={data.sample || paramsSample} rootKey="steps.trigger" />
            </div>
          }
        />
      );
    }

    const trigConfig = (
      <div>
        <Typography className={classes.leadText}>{trig.desc || `${trig.title} · ${trig.direction === "inbound" ? "entrada" : "saída"}`}</Typography>
        {trig.nativeInbound && (
          <Typography className={classes.hint}>
            Recebe automaticamente as mensagens da conta selecionada acima. Não precisa configurar URL.
          </Typography>
        )}
        {isWebhookBased(data.triggerType) && !trig.nativeInbound && (
          <>
            <Typography className={classes.fieldLabel}>{i18n.t("triggers.webhookUrl")}</Typography>
            {webhookUrl ? (
              <>
                <CopyUrlField value={webhookUrl} classes={classes} />
              </>
            ) : (
              <Typography className={classes.hint}>{i18n.t("triggers.webhookSaveHint")}</Typography>
            )}
          </>
        )}
        {data.triggerType === "api_call" && (() => {
                const origin = typeof window !== "undefined" ? window.location.origin : "https://builder.numera.io";
                const endpoint = `${origin}/v1/calls`;
                const curl = [
                  `curl -X POST ${endpoint} \\`,
                  `  -H "Authorization: Bearer SUA_API_KEY" \\`,
                  `  -H "Content-Type: application/json" \\`,
                  `  -d '{`,
                  `    "to": "+5511999999999",`,
                  `    "from": "+5511333333333",`,
                  `    "applicationId": ${appId || "<id>"}`,
                  `  }'`,
                ].join("\n");
                return (
                  <div style={{ marginTop: 4 }}>
                    <Typography className={classes.hint} style={{ marginBottom: 6 }}>
                      A chamada é iniciada pela API. Crie uma chave em <b>Secrets</b> com o escopo
                      <b> “Fazer chamada”</b> e chame:
                    </Typography>
                    <div className={classes.urlBox} style={{ marginBottom: 6 }}>POST {endpoint}</div>
                    <pre
                      style={{
                        margin: 0,
                        padding: 12,
                        background: "#1e1e1e",
                        color: "#d4f7c5",
                        borderRadius: 8,
                        fontSize: 11.5,
                        lineHeight: 1.5,
                        overflowX: "auto",
                        whiteSpace: "pre",
                      }}
                    >
                      {curl}
                    </pre>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      style={{ marginTop: 8, marginBottom: 4 }}
                      onClick={() => { navigator.clipboard.writeText(curl); toast.success("cURL copiado!"); }}
                    >
                      {i18n.t("apps.copy")} cURL
                    </Button>
                    {!appId && (
                      <Typography className={classes.hint} style={{ marginTop: 4 }}>
                        Salve o fluxo para gerar o id da automação.
                      </Typography>
                    )}
                  </div>
                );
              })()}
        {trig.fields.filter(f => (typeof f.showIf === "function" ? !!f.showIf(data) : true)).map(renderField)}
        {data.triggerType !== "tool" && data.triggerType !== "agent" && data.triggerType !== "collector" && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {/* Trocar gatilho: só em edição (é uma alteração do fluxo) */}
            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<SwapIcon />}
                onClick={onPickTrigger}
                style={{ textTransform: "none" }}
              >
                {i18n.t("triggers.change")}
              </Button>
            )}
            {/* Enviar teste: SEMPRE visível (é ação read-only-safe — só dispara um
                evento de teste, não altera o fluxo) */}
            {isWebhookBased(data.triggerType) && !trig.nativeInbound && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => setTestOpen(true)}
                disabled={!webhookUrl}
                title={webhookUrl ? "" : "Salve o fluxo para gerar a URL do webhook"}
                style={{ textTransform: "none" }}
              >
                Enviar teste
              </Button>
            )}
            {data.triggerType === "scheduled" && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<PlayArrowIcon />}
                disabled={!appId}
                title={appId ? "Roda o fluxo agora (não espera o horário)" : "Salve o fluxo primeiro"}
                onClick={async () => {
                  try {
                    await api.post(`/applications/${appId}/run-now`);
                    toast.success("Execução iniciada — acompanhe em Testes & resultados / Eventos.");
                  } catch (e) {
                    toast.error((e.response && e.response.data && e.response.data.error) || "Não foi possível executar agora.");
                  }
                }}
                style={{ textTransform: "none" }}
              >
                Executar agora
              </Button>
            )}
          </div>
        )}
        {/* Coletor: "Executar agora" roda o fluxo uma vez (mesma fila do run-now
            do agendamento) para testar sem esperar o intervalo do Polling. */}
        {data.triggerType === "collector" && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<PlayArrowIcon />}
              disabled={!appId}
              title={appId ? "Roda o coletor agora (não espera o intervalo)" : "Salve o fluxo primeiro"}
              onClick={async () => {
                try {
                  const { data } = await api.post(`/applications/${appId}/collector-run-now`);
                  if (data && data.ok) {
                    toast.success(`Rodada: ${data.records || 0} registro(s), ${data.dispatched || 0} disparo(s) ao consumidor.`);
                  } else {
                    toast.info((data && data.message) || "Rodada sem registros novos.");
                  }
                } catch (e) {
                  toast.error((e.response && e.response.data && e.response.data.error) || "Não foi possível executar agora.");
                }
              }}
              style={{ textTransform: "none" }}
            >
              Executar agora
            </Button>
          </div>
        )}
        {/* Coletor de dados (consumidor): "Executar agora" roda o COLETOR
            selecionado (não o app atual) — que dispara este consumidor 1x por
            registro novo. Mesmo botão/fila do run-now do gatilho do coletor. */}
        {data.triggerType === "data_collector" && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<PlayArrowIcon />}
              disabled={!data.collectorId}
              title={data.collectorId ? "Roda o coletor agora (dispara este consumidor por registro novo)" : "Selecione um coletor primeiro"}
              onClick={async () => {
                try {
                  const { data: r } = await api.post(`/applications/${data.collectorId}/collector-run-now`);
                  if (r && r.ok) {
                    toast.success(`Rodada: ${r.records || 0} registro(s), ${r.dispatched || 0} disparo(s) ao consumidor.`);
                  } else {
                    toast.info((r && r.message) || "Rodada sem registros novos.");
                  }
                } catch (e) {
                  toast.error((e.response && e.response.data && e.response.data.error) || "Não foi possível executar agora.");
                }
              }}
              style={{ textTransform: "none" }}
            >
              Executar agora
            </Button>
          </div>
        )}
      </div>
    );

    const trigTest = (
      <div>
        {inspecting && renderRunIO()}
        {/* Enviar teste: também aqui na aba Testes (além da aba Configurações) */}
        {isWebhookBased(data.triggerType) && !trig.nativeInbound && (
          <Button
            variant="contained"
            disableElevation
            fullWidth
            startIcon={<SendIcon />}
            onClick={() => setTestOpen(true)}
            disabled={!webhookUrl}
            title={webhookUrl ? "" : "Salve o fluxo para gerar a URL do webhook"}
            style={{ background: "#2C353D", color: "#fff", textTransform: "none", fontWeight: 600, boxShadow: "none", marginBottom: 14 }}
          >
            Enviar teste
          </Button>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <Typography className={classes.fieldLabel} style={{ marginBottom: 0 }}>Eventos recebidos</Typography>
          {appId && (
            <IconButton size="small" title={i18n.t("triggers.refresh")} onClick={loadEvents}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        {!appId ? (
          <Typography className={classes.hint}>{i18n.t("triggers.webhookSaveHint")}</Typography>
        ) : events.length === 0 ? (
          <Typography className={classes.hint}>{i18n.t("triggers.noEvents")}</Typography>
        ) : (
          events.map(ev => (
            <div key={ev.id} className={clsx(classes.evItem, selectedEventId === ev.id && classes.evActive)} onClick={() => selectEvent(ev)}>
              <span className={clsx(classes.dot, dotClass(ev.status))} />
              <span className={classes.evMsg}>{ev.message || ev.type}</span>
              <span className={classes.evTime}>{format(new Date(ev.createdAt), "dd/MM HH:mm:ss")}</span>
            </div>
          ))
        )}
        <Typography className={classes.fieldLabel} style={{ marginTop: 16 }}>Variáveis do gatilho</Typography>
        {data.sample ? (
          <JsonTree data={data.sample} rootKey="steps.trigger" />
        ) : trig.out && trig.out.sample ? (
          <>
            <Typography className={classes.hint} style={{ marginBottom: 8 }}>{i18n.t("triggers.schemaHint")}</Typography>
            <JsonTree data={{ event: { body: trig.out.sample } }} rootKey="steps.trigger" />
          </>
        ) : (
          <Typography className={classes.hint}>{i18n.t("triggers.selectEventHint")}</Typography>
        )}
      </div>
    );

    // o "Enviar teste" agora fica ao lado de "Trocar gatilho" (no corpo da config),
    // então o rodapé não repete o botão.
    const trigFooter = null;

    return (
      <>
        <NodeConfigShell
          tab={shellTab}
          onTabChange={setShellTab}
          icon={<NodeIcon triggerType={data.triggerType} color={trig.color} size={34} chip />}
          name={data.label || trig.title}
          nameEditable={!readOnly}
          onName={v => set("label", v)}
          tag="gatilho"
          subtitle={trig.desc}
          onClose={onClose}
          footer={trigFooter}
          config={trigConfig}
          test={trigTest}
        />
        <TriggerTestModal
          open={testOpen}
          webhookUrl={webhookUrl}
          secret={data.secret}
          initialBody={data.sample && data.sample.event && data.sample.event.body}
          published={published}
          dirty={dirty}
          onPublish={onPublish}
          onClose={() => setTestOpen(false)}
          onSent={handleSent}
        />
      </>
    );
  }

  // ---- VERB / BRANCH NODE: casca padrão (header + abas + rodapé) ----
  const accent = nodeAccent(data.verbKey, def.category);
  const verbSubtitle = def.ai ? (String(def.ai).match(/^[^.!?]*[.!?]?/) || [""])[0].trim() : undefined;
  // Campo CONDICIONAL: `showIf(data)` no def do verbo esconde o campo quando ele
  // não se aplica (ex.: Body num GET, que o executor descarta). Melhor que exibir
  // um campo inútil com aviso — o usuário não preenche o que não vê.
  const fieldVisible = f => (typeof f.showIf === "function" ? !!f.showIf(data) : true);
  const advFields = (def.fields || []).filter(f => f.advanced && fieldVisible(f));

  const verbConfig = (
    <div>
      {data.disabled && (
        <Typography className={classes.hint} style={{ color: "#B26A00", marginBottom: 10 }}>
          Nó desativado — será ignorado na publicação e na execução.
        </Typography>
      )}

      {(def.fields || []).filter(f => !f.advanced && fieldVisible(f)).map(renderField)}
      {advFields.length > 0 && (
        <div>
          <div className={classes.advToggle} onClick={() => setAdvOpen(o => !o)}>
            <TuneIcon />
            Avançado (opcional)
            {advOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </div>
          <Collapse in={advOpen}>{advFields.map(renderField)}</Collapse>
        </div>
      )}

      {data.verbKey === "http_request" && !readOnly && (
        <>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<SendIcon />}
            disabled={!data.url}
            title={!data.url ? "Informe a URL" : ""}
            onClick={() => setHttpTestOpen(true)}
            style={{ textTransform: "none", marginTop: 6 }}
          >
            Enviar teste
          </Button>
          <HttpTestModal
            open={httpTestOpen}
            onClose={() => setHttpTestOpen(false)}
            onResult={out => onNodeTested && onNodeTested(node.id, out)}
            node={{ verb: "http_request", method: data.method || "POST", url: data.url, headers: (() => { try { return data.headers ? JSON.parse(data.headers) : {}; } catch { return {}; } })(), body: data.body }}
          />
        </>
      )}

      {def.isIvr && (() => {
        const order = k => (k === "star" ? 100 : k === "pound" ? 101 : Number(k));
        const sortD = arr => [...arr].sort((a, b) => order(a) - order(b));
        const dlabel = k => (k === "star" ? "*" : k === "pound" ? "#" : k);
        const cur = data.digits || [];
        return (
          <div>
            <Typography className={classes.hint}>Dígitos do menu (saídas)</Typography>
            <div className={classes.caseRow}>
              {sortD(cur).map(k => (
                <Chip key={k} size="small" label={dlabel(k)} onDelete={readOnly ? undefined : () => set("digits", cur.filter(x => x !== k))} />
              ))}
            </div>
            {!readOnly && (
              <div className={classes.caseRow}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "star", "pound"]
                  .filter(k => cur.indexOf(k) < 0)
                  .map(k => (
                    <Chip key={k} size="small" variant="outlined" clickable
                      label={dlabel(k)}
                      onClick={() => set("digits", sortD([...cur, k]))} />
                  ))}
              </div>
            )}
            <Typography className={classes.hint} style={{ marginTop: 8 }}>
              Cada dígito é uma saída. "Sem resposta" (timeout) é gerado automaticamente.
            </Typography>
          </div>
        );
      })()}

      {def.isBranch && (() => {
        const keys = data.caseKeys || [];
        const addCase = () => {
          const v = String(newCase || "").trim();
          if (!v) return;
          if (v === "default" || v === "timeout") { toast.error('"default" e "timeout" são reservados.'); return; }
          if (keys.some(k => String(k) === v)) { toast.error("Esse caso já existe."); return; }
          set("caseKeys", [...keys, v]);
          setNewCase("");
        };
        return (
          <div>
            <Typography className={classes.hint}>Casos (saídas do branch)</Typography>
            <div className={classes.caseRow}>
              {keys.map(k => (
                <Chip key={k} size="small" label={k} onDelete={readOnly ? undefined : () => set("caseKeys", keys.filter(x => x !== k))} />
              ))}
            </div>
            {!readOnly && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="valor do caso (ex.: sim, não, 1)"
                  value={newCase}
                  onChange={e => setNewCase(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCase(); } }}
                  style={{ flex: 1 }}
                />
                <Button size="small" variant="outlined" color="secondary" onClick={addCase} disabled={!newCase.trim()}>
                  + caso
                </Button>
              </div>
            )}
            <Typography className={classes.hint} style={{ marginTop: 8 }}>
              O caso é comparado com o valor da variável (texto ou número; ex.: uma opção de URA ou "sim"/"não" de um código). Sem diferenciar maiúsculas/espaços. "default" e "timeout" são gerados automaticamente.
            </Typography>
          </div>
        );
      })()}
    </div>
  );

  const verbTest = (() => {
    const io = renderRunIO();
    if (io) return io;
    return (
      <Typography className={classes.leadText}>
        Execute o fluxo (ou teste) para ver a entrada e a saída deste nó aqui.
      </Typography>
    );
  })();

  const verbFooter = readOnly
    ? editBtn
    : (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span
          onClick={() => set("disabled", !data.disabled)}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12.5, color: theme.app.textSecondary }}
        >
          {data.disabled
            ? <ToggleOffIcon fontSize="small" style={{ color: "#888780" }} />
            : <ToggleOnIcon fontSize="small" color="primary" />}
          {data.disabled ? "Desativado" : "Ativo"}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {onSwapNode && (
            <Button
              size="small"
              onClick={onSwapNode}
              startIcon={<SwapIcon fontSize="small" />}
              style={{ textTransform: "none" }}
            >
              Trocar nó
            </Button>
          )}
          <Button
            size="small"
            onClick={confirmDelete}
            startIcon={<DeleteIcon fontSize="small" />}
            style={{ textTransform: "none", color: theme.app.danger }}
          >
            Excluir nó
          </Button>
        </div>
      </div>
    );

  return (
    <>
      <NodeConfigShell
        tab={shellTab}
        onTabChange={setShellTab}
        icon={<NodeIcon verbKey={data.verbKey} category={def.category} color={accent} size={34} chip />}
        name={data.label || def.title}
        nameEditable={!readOnly}
        onName={v => set("label", v)}
        subtitle={verbSubtitle}
        onClose={onClose}
        footer={verbFooter}
        config={verbConfig}
        test={verbTest}
      />

      <VariablePicker
        open={!!varField}
        scope={scope}
        groups={varGroups}
        runAt={varRunAt}
        onClose={() => setVarField(null)}
        onPick={path => {
          const ins = `{{${path}}}`;
          // "area": inserir o chip no cursor do TokenTextArea ativo (contenteditable)
          if (varField === "area") {
            if (areaInsert.current) areaInsert.current(path);
            setVarField(null);
            return;
          }
          // template variables live in data.params[i] (target "param:<i>");
          // "set:<field>" substitui o valor inteiro (campo de variável única, ex.:
          // o campo do condicional); o resto insere na posição do cursor.
          if (typeof varField === "string" && varField.startsWith("param:")) {
            const i = Number(varField.slice(6));
            const arr = Array.isArray(data.params) ? [...data.params] : [];
            arr[i] = ins; // valor único → substitui pelo dado escolhido (vira chip)
            set("params", arr);
          } else if (typeof varField === "string" && varField.startsWith("set:")) {
            set(varField.slice(4), ins);
          } else {
            const cur = String(data[varField] ?? "");
            const c = caretRef.current;
            if (c && c.field === varField && typeof c.start === "number") {
              set(varField, cur.slice(0, c.start) + ins + cur.slice(c.end));
            } else {
              set(varField, cur + ins);
            }
          }
          setVarField(null);
        }}
      />

      <AudioPicker
        open={!!audioField}
        value={audioField ? data[audioField] : ""}
        kind={audioField && def ? ((def.fields || []).find(f => f.name === audioField) || {}).audioKind : undefined}
        onClose={() => setAudioField(null)}
        onPick={url => {
          if (audioField) set(audioField, url);
          setAudioField(null);
        }}
      />

      {/* IVR "message & voice" modal */}
      <Dialog open={menuModal} onClose={() => setMenuModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mensagem e voz do menu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            label="Texto do menu (o que o chamador ouve)"
            value={data.prompt || ""}
            onChange={e => { const v = e.target.value; set("prompt", v); }}
            disabled={readOnly}
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: "flex", gap: 12 }}>
            <TextField select SelectProps={selectProps} variant="outlined" size="small" label="Provedor" style={{ minWidth: 130 }}
              value={ivrVendor} onChange={e => onChange({ ...data, ttsVendor: e.target.value, voice: "" })} disabled={readOnly}>
              {(vendors.length ? vendors : ["openai"]).map(vd => (
                <MenuItem key={vd} value={vd}>{vd === "elevenlabs" ? "ElevenLabs" : "OpenAI"}</MenuItem>
              ))}
            </TextField>
            <TextField select SelectProps={selectProps} fullWidth variant="outlined" size="small" label="Voz"
              value={data.voice || ""} onChange={e => set("voice", e.target.value)} disabled={readOnly}>
              <MenuItem value="">— padrão —</MenuItem>
              {(voices || []).map(v => (
                <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
              ))}
            </TextField>
            <TextField select SelectProps={selectProps} variant="outlined" size="small" label="Velocidade" style={{ minWidth: 110 }}
              value={data.rate || "normal"} onChange={e => set("rate", e.target.value)} disabled={readOnly}>
              <MenuItem value="slow">Lenta</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="fast">Rápida</MenuItem>
            </TextField>
          </div>
          {voices === null && <Typography className={classes.hint} style={{ marginTop: 8 }}>Carregando vozes…</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => previewVoice(data.voice, data.rate, ivrVendor)} disabled={voiceLoading}
            startIcon={voiceLoading ? <CircularProgress size={14} /> : <PlayArrowIcon fontSize="small" />}>
            Testar voz
          </Button>
          <div style={{ flex: 1 }} />
          <Button onClick={() => setMenuModal(false)} variant="contained" color="primary">Concluir</Button>
        </DialogActions>
      </Dialog>

      {newConn && (
        <ConnectorModal
          open
          item={connByType(newConn.type)}
          onClose={() => setNewConn(null)}
          onSaved={async created => {
            await loadConnectors();
            if (created && created.id) set(newConn.field, created.id);
            setNewConn(null);
          }}
        />
      )}
    </>
  );
};

export default ConfigDrawer;
